import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar as BigCalendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import enUS from 'date-fns/locale/en-US';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { 
  LineChart, 
  Line, 
  AreaChart, 
  Area, 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer
} from 'recharts';
import { 
  LayoutDashboard, 
  Users, 
  Building2,
  FileText, 
  FolderOpen,
  MessageSquare,
  Settings,
  LogOut,
  Bell,
  Search,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Eye,
  CheckCircle2,
  Clock,
  AlertCircle,
  Download,
  ExternalLink,
  Plus,
  Filter,
  MoreVertical,
  ArrowUpRight,
  Calendar,
  Mail,
  Phone,
  User,
  Menu,
  X,
  Send,
  Paperclip,
  FileIcon,
  Trash2,
  Upload,
  File,
  Image,
  RefreshCw,
  Zap,
  BarChart3,
  Activity,
  ArrowRight,
  Star,
  Info,
  AlertTriangle,
  Play,
  Pause,
  Globe,
  Instagram,
  Facebook,
  MapPin,
  Briefcase,
  Target,
  UserPlus,
  ClipboardList,
  CalendarDays,
  Link,
  Edit3,
  Save,
  Copy,
  Check,
  MousePointer,
  BookOpen,
  HelpCircle,
  Headphones,
  Timer,
  Award,
  Sparkles,
  Rocket,
  Shield,
  Video,
  Lightbulb,
  FileQuestion,
  MessageCircle,
  LifeBuoy,
  Inbox,
  ClipboardCheck,
  SendHorizontal,
  RotateCcw,
  Megaphone,
  Maximize2
} from 'lucide-react';
import { useStaffAuth } from '../context/StaffAuthContext';
import { useRole } from '../context/RoleContext';
import CRMPipeline from '../components/crm/CRMPipeline';

const API = process.env.REACT_APP_BACKEND_URL || '';
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_client-dash-revamp/artifacts/wn2edy78_logo_transparent_background.png';

// BigCalendar localizer
const locales = { 'en-US': enUS };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const scaleIn = {
  initial: { opacity: 0, scale: 0.9 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.9 }
};

// ============ MAIN STAFF DASHBOARD ============
export default function StaffDashboardNew() {
  const navigate = useNavigate();
  const { logout, staffUser } = useStaffAuth();
  const { getRoleDisplayName, getRoleBadge, hasPermission, loading: roleLoading } = useRole();
  
  const [activeSection, setActiveSection] = useState('dashboard');
  const [activeSubSection, setActiveSubSection] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [agencyLeads, setAgencyLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [urgentAnnouncements, setUrgentAnnouncements] = useState([]);
  const [dismissedAnnouncements, setDismissedAnnouncements] = useState(() => {
    const saved = localStorage.getItem('dismissedAnnouncements');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    fetchInitialData();
    fetchUrgentAnnouncements();
  }, []);

  const fetchUrgentAnnouncements = async () => {
    try {
      const res = await fetch(`${API}/api/staff/announcements`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        // Filter for high/urgent priority only
        const urgent = (data.announcements || []).filter(
          a => a.priority === 'high' || a.priority === 'urgent'
        );
        setUrgentAnnouncements(urgent);
      }
    } catch (err) {
      console.error('Error fetching announcements:', err);
    }
  };

  const dismissAnnouncement = (announcementId) => {
    const updated = [...dismissedAnnouncements, announcementId];
    setDismissedAnnouncements(updated);
    localStorage.setItem('dismissedAnnouncements', JSON.stringify(updated));
  };

  const visibleAnnouncements = urgentAnnouncements.filter(
    a => !dismissedAnnouncements.includes(a.announcement_id)
  );

  const fetchInitialData = async () => {
    try {
      // Fetch assigned clients
      const clientsRes = await fetch(`${API}/api/staff/clients`, {
        credentials: 'include'
      });
      if (clientsRes.ok) {
        const data = await clientsRes.json();
        setClients(data.clients || []);
      }

      // Fetch agency leads
      const leadsRes = await fetch(`${API}/api/admin/leads`, {
        credentials: 'include'
      });
      if (leadsRes.ok) {
        const data = await leadsRes.json();
        setAgencyLeads(data.leads || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/staff-login', { replace: true });
    } catch (err) {
      navigate('/staff-login', { replace: true });
    }
  };

  const handleSectionChange = (section, subSection = null) => {
    setActiveSection(section);
    setActiveSubSection(subSection);
    if (section !== 'client-workspace') {
      setSelectedClient(null);
    }
    setMobileMenuOpen(false);
  };

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setActiveSection('client-workspace');
    setActiveSubSection('overview');
    setMobileMenuOpen(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex flex-col items-center gap-4"
        >
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-500">Loading dashboard...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc] flex">
      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 z-40 lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <StaffSidebar 
        activeSection={activeSection}
        activeSubSection={activeSubSection}
        onSectionChange={handleSectionChange}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        onLogout={handleLogout}
        clients={clients}
        onClientSelect={handleClientSelect}
        selectedClient={selectedClient}
        getRoleBadge={getRoleBadge}
        getRoleDisplayName={getRoleDisplayName}
        roleLoading={roleLoading}
      />
      
      {/* Main Content */}
      <main className={`flex-1 transition-all duration-300 ${sidebarOpen ? 'lg:ml-72' : 'lg:ml-20'}`}>
        {/* Top Header */}
        <StaffHeader 
          setMobileMenuOpen={setMobileMenuOpen}
          activeSection={activeSection}
          selectedClient={selectedClient}
          staffUser={staffUser}
        />
        
        {/* Urgent Announcements Banner */}
        <AnimatePresence>
          {visibleAnnouncements.length > 0 && (
            <AnnouncementBanner 
              announcements={visibleAnnouncements}
              onDismiss={dismissAnnouncement}
            />
          )}
        </AnimatePresence>
        
        {/* Content Area */}
        <div className="p-4 md:p-6 lg:p-8">
          <AnimatePresence mode="wait">
            {activeSection === 'dashboard' && (
              <StaffDashboardOverview 
                key="dashboard" 
                clients={clients}
                agencyLeads={agencyLeads}
                onClientSelect={handleClientSelect}
                onSectionChange={handleSectionChange}
                staffUser={staffUser}
              />
            )}
            {activeSection === 'clients' && (
              <ClientsListView 
                key="clients" 
                clients={clients}
                onClientSelect={handleClientSelect}
                onRefresh={fetchInitialData}
              />
            )}
            {activeSection === 'client-workspace' && selectedClient && (
              <ClientWorkspace 
                key={`client-${selectedClient.client_id}`}
                client={selectedClient}
                subSection={activeSubSection}
                setSubSection={setActiveSubSection}
                onRefresh={fetchInitialData}
              />
            )}
            {activeSection === 'agency-leads' && (
              <AgencyLeadsView 
                key="agency-leads"
                leads={agencyLeads}
                onRefresh={fetchInitialData}
              />
            )}
            {activeSection === 'agency-appointments' && (
              <AgencyAppointmentsView key="agency-appointments" />
            )}
            {activeSection === 'agency-booking' && (
              <BookingHubSection key="agency-booking" staffUser={staffUser} />
            )}
            {activeSection === 'agency-sop' && (
              <AgencySOPView key="agency-sop" />
            )}
            {activeSection === 'agency-help' && (
              <AgencyHelpView key="agency-help" />
            )}
            {activeSection === 'agency-timetracking' && (
              <AgencyTimeTrackingView key="agency-timetracking" clients={clients} />
            )}
            {activeSection === 'messages' && (
              <StaffMessagesView key="messages" clients={clients} />
            )}
            {activeSection === 'files' && (
              <StaffFilesView key="files" clients={clients} />
            )}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}

// ============ SIDEBAR ============
function StaffSidebar({ 
  activeSection, 
  activeSubSection,
  onSectionChange, 
  sidebarOpen, 
  setSidebarOpen, 
  mobileMenuOpen, 
  setMobileMenuOpen, 
  onLogout,
  clients,
  onClientSelect,
  selectedClient,
  getRoleBadge,
  getRoleDisplayName,
  roleLoading
}) {
  const [expandedGroups, setExpandedGroups] = useState(['client-work', 'agency-ops']);

  const toggleGroup = (group) => {
    setExpandedGroups(prev => 
      prev.includes(group) ? prev.filter(g => g !== group) : [...prev, group]
    );
  };

  const mainNavItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, description: 'Overview & tasks' },
  ];

  const clientWorkItems = [
    { id: 'clients', label: 'My Clients', icon: Users, description: 'Client list' },
  ];

  const agencyOpsItems = [
    { id: 'agency-leads', label: 'Agency Leads', icon: UserPlus, description: 'New prospects' },
    { id: 'agency-appointments', label: 'Appointments', icon: CalendarDays, description: 'Schedule' },
    { id: 'agency-booking', label: 'Booking Hub', icon: Calendar, description: 'Manage bookings' },
    { id: 'agency-sop', label: 'SOPs & Guides', icon: BookOpen, description: 'Procedures' },
    { id: 'agency-help', label: 'Get Help', icon: LifeBuoy, description: 'Support' },
    { id: 'agency-timetracking', label: 'Time Tracking', icon: Timer, description: 'Log hours' },
  ];

  const utilityItems = [
    { id: 'messages', label: 'Messages', icon: MessageSquare, description: 'All conversations' },
    { id: 'files', label: 'Files', icon: FolderOpen, description: 'Documents' },
  ];

  const SidebarContent = ({ isMobile = false }) => (
    <>
      {/* Logo */}
      <div className={`p-4 border-b border-slate-100 flex items-center ${sidebarOpen || isMobile ? 'gap-3' : 'justify-center'}`}>
        <div className="flex-shrink-0">
          <img 
            src={LOGO_URL} 
            alt="BirdenMedia" 
            className={`${sidebarOpen || isMobile ? 'h-10' : 'h-8'} w-auto`}
          />
        </div>
        {(sidebarOpen || isMobile) && (
          <motion.div 
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            className="overflow-hidden"
          >
            <p className="font-semibold text-slate-800 text-sm">BirdenMedia</p>
            <p className="text-xs text-blue-500 font-medium">Staff Portal</p>
          </motion.div>
        )}
        {isMobile && (
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="ml-auto p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-50"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto">
        {/* Main Nav */}
        <div className="px-3 mb-4">
          {mainNavItems.map((item) => (
            <NavItem 
              key={item.id}
              item={item}
              isActive={activeSection === item.id}
              onClick={() => onSectionChange(item.id)}
              sidebarOpen={sidebarOpen || isMobile}
            />
          ))}
        </div>

        {/* Client Work Section */}
        <div className="px-3 mb-4">
          {(sidebarOpen || isMobile) && (
            <button 
              onClick={() => toggleGroup('client-work')}
              className="flex items-center justify-between w-full px-3 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider hover:text-slate-600"
            >
              <span>Client Work</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${expandedGroups.includes('client-work') ? 'rotate-180' : ''}`} />
            </button>
          )}
          {(expandedGroups.includes('client-work') || !sidebarOpen) && (
            <div className="space-y-1">
              {clientWorkItems.map((item) => (
                <NavItem 
                  key={item.id}
                  item={item}
                  isActive={activeSection === item.id || (activeSection === 'client-workspace' && item.id === 'clients')}
                  onClick={() => onSectionChange(item.id)}
                  sidebarOpen={sidebarOpen || isMobile}
                />
              ))}
              {/* Client Quick List */}
              {(sidebarOpen || isMobile) && clients.slice(0, 5).map((client) => (
                <button
                  key={client.client_id}
                  onClick={() => onClientSelect(client)}
                  className={`w-full flex items-center gap-2 px-3 py-2 ml-4 rounded-lg text-sm transition-all ${
                    selectedClient?.client_id === client.client_id
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                  }`}
                >
                  <div className="w-6 h-6 bg-slate-200 rounded-lg flex items-center justify-center text-xs font-semibold">
                    {client.business_name?.charAt(0) || 'C'}
                  </div>
                  <span className="truncate text-xs">{client.business_name || client.short_id}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Agency Operations Section */}
        <div className="px-3 mb-4">
          {(sidebarOpen || isMobile) && (
            <button 
              onClick={() => toggleGroup('agency-ops')}
              className="flex items-center justify-between w-full px-3 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider hover:text-slate-600"
            >
              <span>Agency Ops</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${expandedGroups.includes('agency-ops') ? 'rotate-180' : ''}`} />
            </button>
          )}
          {(expandedGroups.includes('agency-ops') || !sidebarOpen) && (
            <div className="space-y-1">
              {agencyOpsItems.map((item) => (
                <NavItem 
                  key={item.id}
                  item={item}
                  isActive={activeSection === item.id}
                  onClick={() => onSectionChange(item.id)}
                  sidebarOpen={sidebarOpen || isMobile}
                />
              ))}
            </div>
          )}
        </div>

        {/* Utility Items */}
        <div className="px-3 pt-4 border-t border-slate-100">
          {utilityItems.map((item) => (
            <NavItem 
              key={item.id}
              item={item}
              isActive={activeSection === item.id}
              onClick={() => onSectionChange(item.id)}
              sidebarOpen={sidebarOpen || isMobile}
            />
          ))}
        </div>
      </nav>

      {/* Bottom Section */}
      <div className="p-3 border-t border-slate-100">
        {(sidebarOpen || isMobile) && !roleLoading && (
          <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 px-3 py-2 rounded-xl mb-3">
            <span className="px-2 py-0.5 text-xs font-bold rounded bg-blue-500 text-white">
              {getRoleBadge()}
            </span>
            <span className="text-xs text-slate-600">{getRoleDisplayName()}</span>
          </div>
        )}
        <button
          onClick={onLogout}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-slate-600 
            hover:bg-red-50 hover:text-red-600 transition-all duration-200
            ${!(sidebarOpen || isMobile) ? 'justify-center' : ''}`}
        >
          <LogOut className="w-5 h-5" />
          {(sidebarOpen || isMobile) && <span className="text-sm">Sign Out</span>}
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className={`
        fixed left-0 top-0 h-screen bg-white border-r border-slate-200 
        flex-col z-50 transition-all duration-300 hidden lg:flex shadow-sm
        ${sidebarOpen ? 'w-72' : 'w-20'}
      `}>
        <SidebarContent />

        {/* Collapse Toggle */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="absolute -right-3 top-20 w-6 h-6 bg-white border border-slate-200 rounded-full flex items-center justify-center hover:bg-slate-50 transition-colors shadow-sm"
        >
          {sidebarOpen ? (
            <ChevronLeft className="w-4 h-4 text-slate-400" />
          ) : (
            <ChevronRight className="w-4 h-4 text-slate-400" />
          )}
        </button>
      </aside>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.aside
            initial={{ x: -300 }}
            animate={{ x: 0 }}
            exit={{ x: -300 }}
            transition={{ type: 'spring', damping: 25 }}
            className="fixed left-0 top-0 h-screen w-[300px] bg-white border-r border-slate-200 flex flex-col z-50 lg:hidden shadow-xl"
          >
            <SidebarContent isMobile={true} />
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );
}

// ============ ANNOUNCEMENT BANNER ============
function AnnouncementBanner({ announcements, onDismiss }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // Auto-cycle through announcements every 8 seconds if multiple
  useEffect(() => {
    if (announcements.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % announcements.length);
    }, 8000);
    return () => clearInterval(timer);
  }, [announcements.length]);

  if (announcements.length === 0) return null;

  const current = announcements[currentIndex];
  const isUrgent = current.priority === 'urgent';

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      className={`border-b ${
        isUrgent 
          ? 'bg-gradient-to-r from-red-500 to-red-600 text-white' 
          : 'bg-gradient-to-r from-amber-400 to-amber-500 text-amber-900'
      }`}
      data-testid="announcement-banner"
    >
      <div className="px-4 md:px-6 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className={`p-1.5 rounded-lg ${isUrgent ? 'bg-white/20' : 'bg-amber-600/20'}`}>
              {isUrgent ? (
                <AlertTriangle className="w-5 h-5" />
              ) : (
                <Megaphone className="w-5 h-5" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className={`text-xs font-bold uppercase tracking-wider ${
                  isUrgent ? 'text-red-100' : 'text-amber-700'
                }`}>
                  {isUrgent ? 'URGENT' : 'ANNOUNCEMENT'}
                </span>
                {announcements.length > 1 && (
                  <span className={`text-xs ${isUrgent ? 'text-red-200' : 'text-amber-700'}`}>
                    ({currentIndex + 1}/{announcements.length})
                  </span>
                )}
              </div>
              <p className="font-semibold truncate">{current.title}</p>
              <p className={`text-sm truncate ${isUrgent ? 'text-red-100' : 'text-amber-800'}`}>
                {current.message}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 flex-shrink-0">
            {announcements.length > 1 && (
              <div className="flex gap-1">
                {announcements.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentIndex(idx)}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      idx === currentIndex 
                        ? (isUrgent ? 'bg-white' : 'bg-amber-800') 
                        : (isUrgent ? 'bg-white/40' : 'bg-amber-700/40')
                    }`}
                  />
                ))}
              </div>
            )}
            <button
              onClick={() => onDismiss(current.announcement_id)}
              className={`p-1.5 rounded-lg transition-colors ${
                isUrgent 
                  ? 'hover:bg-white/20 text-white' 
                  : 'hover:bg-amber-600/20 text-amber-800'
              }`}
              title="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ============ NAV ITEM ============
function NavItem({ item, isActive, onClick, sidebarOpen }) {
  return (
    <button
      onClick={onClick}
      className={`
        w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200
        ${isActive
          ? 'bg-blue-50 text-blue-600 font-medium'
          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
        }
        ${!sidebarOpen ? 'justify-center' : ''}
      `}
      title={!sidebarOpen ? item.label : undefined}
    >
      <item.icon className="w-5 h-5 flex-shrink-0" />
      {sidebarOpen && <span className="text-sm">{item.label}</span>}
    </button>
  );
}

// ============ HEADER ============
function StaffHeader({ setMobileMenuOpen, activeSection, selectedClient, staffUser }) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const notificationRef = useRef(null);

  // Fetch notification count on mount and poll every 30 seconds
  useEffect(() => {
    fetchNotificationCount();
    const interval = setInterval(fetchNotificationCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notificationRef.current && !notificationRef.current.contains(e.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNotificationCount = async () => {
    try {
      const res = await fetch(`${API}/api/staff/notifications/count`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setUnreadCount(data.unread_count || 0);
      }
    } catch (err) {
      console.error('Error fetching notification count:', err);
    }
  };

  const fetchNotifications = async () => {
    setLoadingNotifications(true);
    try {
      const res = await fetch(`${API}/api/staff/notifications?limit=10`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoadingNotifications(false);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await fetch(`${API}/api/staff/notifications/mark-all-read`, {
        method: 'POST',
        credentials: 'include'
      });
      setUnreadCount(0);
      setNotifications(notifications.map(n => ({ ...n, read: true })));
    } catch (err) {
      console.error('Error marking notifications read:', err);
    }
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      try {
        await fetch(`${API}/api/staff/notifications/${notification.notification_id}/read`, {
          method: 'POST',
          credentials: 'include'
        });
        setUnreadCount(prev => Math.max(0, prev - 1));
        setNotifications(notifications.map(n => 
          n.notification_id === notification.notification_id ? { ...n, read: true } : n
        ));
      } catch (err) {
        console.error('Error marking notification read:', err);
      }
    }
    setShowNotifications(false);
  };

  const toggleNotifications = () => {
    if (!showNotifications) {
      fetchNotifications();
    }
    setShowNotifications(!showNotifications);
  };

  const formatTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const getPageTitle = () => {
    if (activeSection === 'client-workspace' && selectedClient) {
      return selectedClient.business_name || 'Client Workspace';
    }
    const titles = {
      'dashboard': 'Dashboard',
      'clients': 'My Clients',
      'agency-leads': 'Agency Leads',
      'agency-appointments': 'Appointments',
      'agency-booking': 'Booking Hub',
      'agency-sop': 'SOPs & Guides',
      'agency-help': 'Get Help',
      'agency-timetracking': 'Time Tracking',
      'messages': 'Messages',
      'files': 'Files'
    };
    return titles[activeSection] || 'Staff Portal';
  };

  return (
    <header className="bg-white border-b border-slate-200 px-4 md:px-6 py-4 sticky top-0 z-30">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="lg:hidden p-2 text-slate-500 hover:text-slate-700 rounded-lg hover:bg-slate-100"
          >
            <Menu className="w-5 h-5" />
          </button>
          
          <div>
            <h1 className="text-lg md:text-xl font-bold text-slate-800">
              {getPageTitle()}
            </h1>
            {activeSection === 'client-workspace' && selectedClient && (
              <p className="text-sm text-slate-500">{selectedClient.short_id}</p>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-2 md:gap-4">
          {/* Search */}
          <div className="relative hidden md:block">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search..."
              className="w-48 lg:w-64 pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
            />
          </div>
          
          {/* Notifications */}
          <div className="relative" ref={notificationRef}>
            <button 
              onClick={toggleNotifications}
              className="relative p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-colors"
              data-testid="notifications-bell"
            >
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {/* Notification Dropdown */}
            <AnimatePresence>
              {showNotifications && (
                <motion.div
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  className="absolute right-0 top-full mt-2 w-80 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden z-50"
                  data-testid="notifications-dropdown"
                >
                  {/* Header */}
                  <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                    <h3 className="font-semibold text-slate-800">Notifications</h3>
                    {unreadCount > 0 && (
                      <button
                        onClick={handleMarkAllRead}
                        className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                      >
                        Mark all read
                      </button>
                    )}
                  </div>

                  {/* Notifications List */}
                  <div className="max-h-80 overflow-y-auto">
                    {loadingNotifications ? (
                      <div className="flex items-center justify-center p-8">
                        <RefreshCw className="w-5 h-5 animate-spin text-slate-400" />
                      </div>
                    ) : notifications.length === 0 ? (
                      <div className="p-8 text-center">
                        <Bell className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                        <p className="text-sm text-slate-500">No notifications yet</p>
                      </div>
                    ) : (
                      notifications.map(notification => (
                        <div
                          key={notification.notification_id}
                          onClick={() => handleNotificationClick(notification)}
                          className={`p-4 border-b border-slate-50 hover:bg-slate-50 cursor-pointer transition-colors ${
                            !notification.read ? 'bg-blue-50/50' : ''
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                              notification.type === 'task_comment' ? 'bg-blue-100 text-blue-600' :
                              notification.type === 'announcement' ? 'bg-purple-100 text-purple-600' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {notification.type === 'task_comment' ? (
                                <MessageSquare className="w-4 h-4" />
                              ) : notification.type === 'announcement' ? (
                                <Bell className="w-4 h-4" />
                              ) : (
                                <Info className="w-4 h-4" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm ${!notification.read ? 'font-medium text-slate-800' : 'text-slate-600'}`}>
                                {notification.title}
                              </p>
                              <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">
                                {notification.message}
                              </p>
                              <p className="text-xs text-slate-400 mt-1">
                                {formatTimeAgo(notification.created_at)}
                              </p>
                            </div>
                            {!notification.read && (
                              <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          
          {/* Profile */}
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-sm">
              <span className="text-sm font-semibold text-white">
                {staffUser?.full_name?.charAt(0) || 'S'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

// ============ DASHBOARD OVERVIEW ============
function StaffDashboardOverview({ clients, agencyLeads, onClientSelect, onSectionChange, staffUser }) {
  const [dashboardStats, setDashboardStats] = useState({
    pendingTasks: 0,
    unreadMessages: 0,
    recentActivity: [],
    todaysTasks: []
  });
  const [loading, setLoading] = useState(true);

  const newLeadsToday = agencyLeads.filter(l => l.status === 'new').length;

  // Get first name from full name
  const firstName = staffUser?.full_name?.split(' ')[0] || 'there';
  
  // Get greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  useEffect(() => {
    fetchDashboardData();
  }, [clients]);

  const fetchDashboardData = async () => {
    try {
      // Fetch unread messages count
      let unreadMessages = 0;
      try {
        const msgRes = await fetch(`${API}/api/staff/messages/inbox/all`, { credentials: 'include' });
        if (msgRes.ok) {
          const msgData = await msgRes.json();
          unreadMessages = (msgData.conversations || []).reduce((sum, c) => sum + (c.unread_count || 0), 0);
        }
      } catch (e) { console.log('Messages not available'); }

      // Fetch tasks from all clients
      let allTasks = [];
      for (const client of clients.slice(0, 5)) { // Limit to first 5 clients for performance
        try {
          const taskRes = await fetch(`${API}/api/staff/tasks/${client.client_id}`, { credentials: 'include' });
          if (taskRes.ok) {
            const taskData = await taskRes.json();
            allTasks = [...allTasks, ...(taskData.tasks || []).map(t => ({ ...t, clientName: client.business_name }))];
          }
        } catch (e) { /* skip */ }
      }

      // Filter and sort tasks
      const pendingTasks = allTasks.filter(t => !t.completed).length;
      const todaysTasks = allTasks
        .filter(t => !t.completed)
        .sort((a, b) => (a.priority === 'high' ? -1 : 1))
        .slice(0, 4)
        .map(t => ({
          id: t.id,
          title: t.title,
          client: t.clientName || 'Unknown',
          priority: t.priority || 'medium',
          completed: t.completed || false
        }));

      // Fetch recent notifications for activity
      let recentActivity = [];
      try {
        const notifRes = await fetch(`${API}/api/staff/notifications?limit=4`, { credentials: 'include' });
        if (notifRes.ok) {
          const notifData = await notifRes.json();
          recentActivity = (notifData.notifications || []).map(n => ({
            type: n.type || 'info',
            text: n.title || n.message,
            time: formatTimeAgo(n.created_at),
            icon: n.type === 'task_comment' ? MessageSquare : n.type === 'lead' ? UserPlus : Bell,
            color: n.type === 'task_comment' ? 'green' : n.type === 'lead' ? 'blue' : 'purple'
          }));
        }
      } catch (e) { console.log('Notifications not available'); }

      // Add leads to activity if we have few notifications
      if (recentActivity.length < 4 && agencyLeads.length > 0) {
        const leadActivities = agencyLeads.slice(0, 4 - recentActivity.length).map(l => ({
          type: 'lead',
          text: `New lead: ${l.first_name} ${l.last_name}`,
          time: formatTimeAgo(l.created_at),
          icon: UserPlus,
          color: 'blue'
        }));
        recentActivity = [...recentActivity, ...leadActivities];
      }

      // Fallback activity if still empty
      if (recentActivity.length === 0) {
        recentActivity = [
          { type: 'info', text: 'Welcome to your dashboard!', time: 'Just now', icon: Bell, color: 'blue' }
        ];
      }

      setDashboardStats({
        pendingTasks,
        unreadMessages,
        recentActivity,
        todaysTasks
      });
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return 'Recently';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const { pendingTasks, unreadMessages, recentActivity, todaysTasks } = dashboardStats;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-6"
    >
      {/* Welcome Banner */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl p-6 text-white relative overflow-hidden"
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -translate-y-1/2 translate-x-1/2" />
        </div>
        <div className="relative">
          <h2 className="text-2xl font-bold mb-2">{getGreeting()}, {firstName}! 👋</h2>
          <p className="text-blue-100">You have {pendingTasks} tasks to complete today and {newLeadsToday} new agency leads.</p>
        </div>
      </motion.div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <QuickStatCard 
          title="Assigned Clients" 
          value={clients.length} 
          icon={Users} 
          color="blue"
          onClick={() => onSectionChange('clients')}
        />
        <QuickStatCard 
          title="Pending Tasks" 
          value={pendingTasks} 
          icon={ClipboardList} 
          color="amber"
        />
        <QuickStatCard 
          title="Unread Messages" 
          value={unreadMessages} 
          icon={MessageSquare} 
          color="green"
          onClick={() => onSectionChange('messages')}
        />
        <QuickStatCard 
          title="Agency Leads" 
          value={newLeadsToday} 
          subtext="new today"
          icon={UserPlus} 
          color="purple"
          onClick={() => onSectionChange('agency-leads')}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Today's Tasks */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-800">Today's Tasks</h3>
            <button className="text-sm text-blue-600 hover:text-blue-700 font-medium">View All</button>
          </div>
          <div className="space-y-3">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-5 h-5 text-slate-400 animate-spin" />
              </div>
            ) : todaysTasks.length === 0 ? (
              <div className="text-center py-8">
                <ClipboardList className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-500 text-sm">No pending tasks</p>
                <p className="text-slate-400 text-xs">Tasks from client workspaces will appear here</p>
              </div>
            ) : (
              todaysTasks.map((task) => (
                <div 
                  key={task.id}
                  className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${
                    task.completed ? 'bg-slate-50' : 'bg-white border border-slate-200 hover:border-blue-300'
                  }`}
                >
                  <button className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                    task.completed 
                      ? 'bg-green-500 border-green-500' 
                      : 'border-slate-300 hover:border-blue-500'
                  }`}>
                    {task.completed && <Check className="w-3 h-3 text-white" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${task.completed ? 'text-slate-400 line-through' : 'text-slate-800'}`}>
                      {task.title}
                    </p>
                    <p className="text-xs text-slate-500">{task.client}</p>
                  </div>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-lg ${
                    task.priority === 'high' 
                      ? 'bg-red-50 text-red-600' 
                      : 'bg-amber-50 text-amber-600'
                  }`}>
                    {task.priority}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
          <h3 className="font-semibold text-slate-800 mb-4">Recent Activity</h3>
          <div className="space-y-3">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-5 h-5 text-slate-400 animate-spin" />
              </div>
            ) : recentActivity.length === 0 ? (
              <div className="text-center py-8">
                <Activity className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-500 text-sm">No recent activity</p>
              </div>
            ) : (
              recentActivity.map((activity, idx) => {
                const Icon = activity.icon;
                const colors = {
                  blue: 'bg-blue-50 text-blue-500',
                  green: 'bg-green-50 text-green-500',
                  amber: 'bg-amber-50 text-amber-500',
                  purple: 'bg-purple-50 text-purple-500',
                };
                return (
                  <div key={idx} className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${colors[activity.color] || 'bg-slate-100 text-slate-500'}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-700">{activity.text}</p>
                      <p className="text-xs text-slate-400">{activity.time}</p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Client Quick Access */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-800">My Clients</h3>
          <button 
            onClick={() => onSectionChange('clients')}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
          >
            View All <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {clients.slice(0, 4).map((client) => (
            <button
              key={client.client_id}
              onClick={() => onClientSelect(client)}
              className="p-4 bg-slate-50 hover:bg-slate-100 rounded-xl text-left transition-colors group"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center text-white font-bold mb-3">
                {client.business_name?.charAt(0) || 'C'}
              </div>
              <p className="font-medium text-slate-800 truncate group-hover:text-blue-600">
                {client.business_name || 'Unnamed Client'}
              </p>
              <p className="text-xs text-slate-500">{client.short_id}</p>
              <div className="flex items-center gap-1 mt-2">
                <span className={`w-2 h-2 rounded-full ${
                  client.client_status === 'active' ? 'bg-green-500' : 'bg-slate-300'
                }`} />
                <span className="text-xs text-slate-500 capitalize">{client.client_status || 'active'}</span>
              </div>
            </button>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

// ============ QUICK STAT CARD ============
function QuickStatCard({ title, value, subtext, icon: Icon, color, onClick }) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-500',
    green: 'bg-green-50 text-green-500',
    amber: 'bg-amber-50 text-amber-500',
    purple: 'bg-purple-50 text-purple-500',
  };

  return (
    <motion.button
      onClick={onClick}
      whileHover={{ y: -2 }}
      className="bg-white border border-slate-200 rounded-2xl p-4 text-left hover:shadow-md transition-all duration-200 w-full"
    >
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <p className="text-2xl font-bold text-slate-800">{value}</p>
      <p className="text-xs text-slate-500">{subtext || title}</p>
    </motion.button>
  );
}

// ============ CLIENTS LIST VIEW ============
function ClientsListView({ clients, onClientSelect, onRefresh }) {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);

  const filteredClients = clients.filter(client => {
    const matchesSearch = 
      client.business_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.short_id?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || client.client_status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const openFullProfile = (e, client) => {
    e.stopPropagation(); // Prevent card click
    navigate(`/staff-d/client/${client.client_id}`);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-6"
      data-testid="clients-list-view"
    >
      {/* Header with Create Button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800">My Clients</h2>
          <p className="text-sm text-slate-500">{clients.length} total clients</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-xl flex items-center gap-2 transition-colors"
          data-testid="create-client-btn"
        >
          <Plus className="w-4 h-4" />
          New Client
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search clients..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="onboarding">Onboarding</option>
        </select>
      </div>

      {/* Client Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredClients.map((client) => (
          <motion.div
            key={client.client_id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -4 }}
            className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-lg transition-all duration-200 cursor-pointer"
            onClick={() => onClientSelect(client)}
            data-testid={`client-card-${client.client_id}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center text-white font-bold text-xl">
                {client.business_name?.charAt(0) || 'C'}
              </div>
              <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${
                client.client_status === 'active' 
                  ? 'bg-green-50 text-green-600' 
                  : client.client_status === 'paused'
                  ? 'bg-amber-50 text-amber-600'
                  : 'bg-slate-100 text-slate-600'
              }`}>
                {client.client_status || 'Active'}
              </span>
            </div>
            <h3 className="font-semibold text-slate-800 mb-1">{client.business_name || 'Unnamed Client'}</h3>
            <p className="text-sm text-slate-500 mb-3">{client.short_id}</p>
            
            <div className="flex items-center gap-4 text-xs text-slate-500">
              <div className="flex items-center gap-1">
                <Target className="w-3.5 h-3.5" />
                <span>{client.plan_tier || 'Growth'}</span>
              </div>
              <div className="flex items-center gap-1">
                <DollarSign className="w-3.5 h-3.5" />
                <span>${client.monthly_fee || 0}/mo</span>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
              <button
                onClick={(e) => openFullProfile(e, client)}
                className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                data-testid={`view-profile-${client.client_id}`}
              >
                <Eye className="w-3.5 h-3.5" />
                View Profile
              </button>
              <ChevronRight className="w-4 h-4 text-slate-400" />
            </div>
          </motion.div>
        ))}
      </div>

      {filteredClients.length === 0 && (
        <div className="text-center py-12">
          <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="font-semibold text-slate-800 mb-2">No clients found</h3>
          <p className="text-slate-500 text-sm">Try adjusting your search or filters</p>
        </div>
      )}

      {/* Create Client Modal */}
      {showCreateModal && (
        <CreateClientModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            onRefresh();
          }}
        />
      )}
    </motion.div>
  );
}

// ============ CREATE CLIENT MODAL ============
function CreateClientModal({ onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    business_name: '',
    primary_contact_first_name: '',
    primary_contact_last_name: '',
    primary_contact_email: '',
    primary_contact_phone: '',
    plan_tier: 'growth',
    monthly_fee: '',
    client_status: 'onboarding',
    start_date: new Date().toISOString().split('T')[0]
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      const response = await fetch(`${API}/api/admin/clients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          monthly_fee: formData.monthly_fee ? parseFloat(formData.monthly_fee) : 0
        })
      });

      if (response.ok) {
        onSuccess();
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to create client');
      }
    } catch (err) {
      console.error('Error creating client:', err);
      setError('Failed to create client. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-800">Create New Client</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Business Name *</label>
            <input
              type="text"
              required
              value={formData.business_name}
              onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
              placeholder="e.g., ABC Roofing Co."
              className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">First Name *</label>
              <input
                type="text"
                required
                value={formData.primary_contact_first_name}
                onChange={(e) => setFormData({ ...formData, primary_contact_first_name: e.target.value })}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Last Name *</label>
              <input
                type="text"
                required
                value={formData.primary_contact_last_name}
                onChange={(e) => setFormData({ ...formData, primary_contact_last_name: e.target.value })}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
              <input
                type="email"
                required
                value={formData.primary_contact_email}
                onChange={(e) => setFormData({ ...formData, primary_contact_email: e.target.value })}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Phone *</label>
              <input
                type="tel"
                required
                value={formData.primary_contact_phone}
                onChange={(e) => setFormData({ ...formData, primary_contact_phone: e.target.value })}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Plan Tier</label>
              <select
                value={formData.plan_tier}
                onChange={(e) => setFormData({ ...formData, plan_tier: e.target.value })}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              >
                <option value="starter">Starter</option>
                <option value="growth">Growth</option>
                <option value="partner">Partner</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Monthly Fee ($)</label>
              <input
                type="number"
                value={formData.monthly_fee}
                onChange={(e) => setFormData({ ...formData, monthly_fee: e.target.value })}
                placeholder="0"
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
            <select
              value={formData.client_status}
              onChange={(e) => setFormData({ ...formData, client_status: e.target.value })}
              className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            >
              <option value="onboarding">Onboarding</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
            </select>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-600 rounded-xl hover:bg-slate-50 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !formData.business_name || !formData.primary_contact_email}
              className="flex-1 px-4 py-2.5 bg-blue-500 hover:bg-blue-600 disabled:bg-slate-300 text-white rounded-xl font-medium flex items-center justify-center gap-2"
            >
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Create Client
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

// ============ CLIENT WORKSPACE ============
function ClientWorkspace({ client, subSection, setSubSection, onRefresh }) {
  const navigate = useNavigate();
  const tabs = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'tasks', label: 'Tasks', icon: ClipboardList },
    { id: 'approvals', label: 'Approvals', icon: ClipboardCheck },
    { id: 'leads', label: 'Leads', icon: Users },
    { id: 'ads', label: 'Ads & Campaigns', icon: Target },
    { id: 'landing-page', label: 'Landing Page', icon: Globe },
    { id: 'files', label: 'Files', icon: FolderOpen },
    { id: 'messages', label: 'Messages', icon: MessageSquare },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  // Plan tier configuration
  const getTierConfig = (tier) => {
    const tierLower = (tier || 'growth').toLowerCase();
    switch (tierLower) {
      case 'starter':
        return {
          gradient: 'from-slate-500 via-slate-600 to-slate-700',
          badge: 'bg-slate-400/20 text-slate-100 border-slate-400/30',
          label: 'Starter',
          icon: '🌱',
          features: { google: true, facebook: false, instagram: false, analytics: true }
        };
      case 'partner':
        return {
          gradient: 'from-amber-500 via-yellow-500 to-orange-500',
          badge: 'bg-amber-400/20 text-amber-100 border-amber-400/30',
          label: 'Partner',
          icon: '👑',
          features: { google: true, facebook: true, instagram: true, analytics: true, priority: true }
        };
      case 'growth':
      default:
        return {
          gradient: 'from-blue-500 via-blue-600 to-indigo-600',
          badge: 'bg-blue-400/20 text-blue-100 border-blue-400/30',
          label: 'Growth',
          icon: '🚀',
          features: { google: true, facebook: true, instagram: false, analytics: true }
        };
    }
  };

  // Status configuration
  const getStatusConfig = (status) => {
    const statusLower = (status || 'active').toLowerCase();
    switch (statusLower) {
      case 'onboarding':
        return { color: 'bg-purple-400/20 text-purple-100 border-purple-400/30', label: 'Onboarding', icon: '🎯' };
      case 'active':
        return { color: 'bg-green-400/20 text-green-100 border-green-400/30', label: 'Active', icon: '✅' };
      case 'review':
        return { color: 'bg-amber-400/20 text-amber-100 border-amber-400/30', label: 'Needs Review', icon: '👀' };
      case 'scaling':
        return { color: 'bg-blue-400/20 text-blue-100 border-blue-400/30', label: 'Scaling', icon: '📈' };
      case 'paused':
        return { color: 'bg-slate-400/20 text-slate-100 border-slate-400/30', label: 'Paused', icon: '⏸️' };
      case 'at_risk':
        return { color: 'bg-red-400/20 text-red-100 border-red-400/30', label: 'At Risk', icon: '⚠️' };
      default:
        return { color: 'bg-green-400/20 text-green-100 border-green-400/30', label: status, icon: '✅' };
    }
  };

  const tierConfig = getTierConfig(client.plan_tier);
  const statusConfig = getStatusConfig(client.client_status);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-6"
    >
      {/* Client Profile Header - Tier Color Coded */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`bg-gradient-to-r ${tierConfig.gradient} rounded-2xl p-6 text-white relative overflow-hidden`}
      >
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-white rounded-full translate-y-1/2 -translate-x-1/2" />
        </div>
        
        <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="flex items-start gap-5">
            <motion.div 
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', delay: 0.2 }}
              className="w-20 h-20 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center text-3xl font-bold border-2 border-white/30"
            >
              {client.business_name?.charAt(0) || 'C'}
            </motion.div>
            <div>
              <div className="flex items-center gap-3 mb-1 flex-wrap">
                <h2 className="text-2xl font-bold">{client.business_name}</h2>
                <span className={`px-2.5 py-1 text-xs font-semibold rounded-full border ${statusConfig.color}`}>
                  {statusConfig.icon} {statusConfig.label}
                </span>
              </div>
              <p className="text-white/80 text-sm mb-2">{client.short_id} • {client.industry || 'Business'}</p>
              
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <a href={`mailto:${client.primary_contact_email}`} className="flex items-center gap-1.5 text-white/80 hover:text-white transition-colors">
                  <Mail className="w-4 h-4" />
                  {client.primary_contact_email || 'No email'}
                </a>
                <a href={`tel:${client.primary_contact_phone}`} className="flex items-center gap-1.5 text-white/80 hover:text-white transition-colors">
                  <Phone className="w-4 h-4" />
                  {client.primary_contact_phone || 'No phone'}
                </a>
                <span className="flex items-center gap-1.5 text-white/80">
                  <MapPin className="w-4 h-4" />
                  {client.business_address?.split(',')[1]?.trim() || 'Location'}
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex flex-wrap items-center gap-3">
            {/* Plan Tier Badge */}
            <div className="text-right mr-4 hidden lg:block">
              <p className="text-white/70 text-xs">Plan</p>
              <div className="flex items-center gap-2">
                <span className="text-xl">{tierConfig.icon}</span>
                <p className="text-2xl font-bold">{tierConfig.label}</p>
              </div>
            </div>
            <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate(`/staff-d/client/${client.client_id}`)}
              className="px-4 py-2.5 bg-white/20 hover:bg-white/30 backdrop-blur text-white text-sm font-medium rounded-xl transition-all flex items-center gap-2 border border-white/20"
              data-testid="open-full-profile-btn"
            >
              <Maximize2 className="w-4 h-4" />
              Full Profile
            </motion.button>
            <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-4 py-2.5 bg-white/20 hover:bg-white/30 backdrop-blur text-white text-sm font-medium rounded-xl transition-all flex items-center gap-2 border border-white/20"
            >
              <ExternalLink className="w-4 h-4" />
              Landing Page
            </motion.button>
            <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-4 py-2.5 bg-white text-slate-800 text-sm font-semibold rounded-xl transition-all flex items-center gap-2 shadow-lg"
            >
              <MessageSquare className="w-4 h-4" />
              Message Client
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* Quick Actions Bar - Feature Based on Tier */}
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="flex gap-2 overflow-x-auto pb-2"
      >
        {tierConfig.features.google && (
          <QuickActionButton icon={Globe} label="Google Ads" url={client.google_ads_account ? `https://ads.google.com/aw/campaigns?ocid=${client.google_ads_account}` : '#'} color="blue" />
        )}
        {tierConfig.features.facebook && (
          <QuickActionButton icon={Facebook} label="Facebook Ads" url={client.facebook_page_url || '#'} color="indigo" />
        )}
        {tierConfig.features.instagram && (
          <QuickActionButton icon={Instagram} label="Instagram" url={client.instagram_handle ? `https://instagram.com/${client.instagram_handle}` : '#'} color="pink" />
        )}
        {tierConfig.features.analytics && (
          <QuickActionButton icon={BarChart3} label="Analytics" url="#" color="purple" />
        )}
        <QuickActionButton icon={FileText} label="Reports" url="#" color="green" />
        {tierConfig.features.priority && (
          <QuickActionButton icon={Star} label="Priority Support" url="#" color="amber" />
        )}
      </motion.div>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto pb-2 scrollbar-hide">
        {tabs.map((tab, idx) => (
          <motion.button
            key={tab.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 * idx }}
            onClick={() => setSubSection(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
              subSection === tab.id
                ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/25'
                : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </motion.button>
        ))}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={subSection}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.2 }}
        >
          {subSection === 'overview' && <ClientOverviewTab client={client} setSubSection={setSubSection} />}
          {subSection === 'tasks' && <ClientTasksTab client={client} />}
          {subSection === 'approvals' && <ClientApprovalsTab client={client} />}
          {subSection === 'leads' && <ClientLeadsTab client={client} onRefresh={onRefresh} />}
          {subSection === 'ads' && <ClientAdsTab client={client} />}
          {subSection === 'landing-page' && <ClientLandingPageTab client={client} />}
          {subSection === 'files' && <ClientFilesTab client={client} />}
          {subSection === 'messages' && <ClientMessagesTab client={client} />}
          {subSection === 'settings' && <ClientSettingsTab client={client} onRefresh={onRefresh} />}
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
}

// Quick Action Button
function QuickActionButton({ icon: Icon, label, url, color }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600 hover:bg-blue-100 border-blue-200',
    indigo: 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100 border-indigo-200',
    pink: 'bg-pink-50 text-pink-600 hover:bg-pink-100 border-pink-200',
    purple: 'bg-purple-50 text-purple-600 hover:bg-purple-100 border-purple-200',
    green: 'bg-green-50 text-green-600 hover:bg-green-100 border-green-200',
  };
  
  return (
    <motion.a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      whileHover={{ scale: 1.02, y: -2 }}
      whileTap={{ scale: 0.98 }}
      className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border transition-all ${colors[color]}`}
    >
      <Icon className="w-4 h-4" />
      {label}
      <ExternalLink className="w-3 h-3 opacity-50" />
    </motion.a>
  );
}

// ============ INTERNAL NOTES CARD ============
function InternalNotesCard({ client }) {
  const [isEditing, setIsEditing] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [noteHistory, setNoteHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Fetch notes on mount
  useEffect(() => {
    fetchNotes();
  }, [client.client_id]);

  const fetchNotes = async () => {
    try {
      const response = await fetch(`${API}/api/staff/notes/${client.client_id}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setNoteHistory(data.notes || []);
      }
    } catch (error) {
      console.error('Error fetching notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNote = async () => {
    if (!noteText.trim()) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${API}/api/staff/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          client_id: client.client_id,
          text: noteText.trim()
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setNoteHistory([data.note, ...noteHistory]);
        setNoteText('');
        setIsEditing(false);
      }
    } catch (error) {
      console.error('Error saving note:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      const response = await fetch(`${API}/api/staff/notes/${noteId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        setNoteHistory(noteHistory.filter(n => n.id !== noteId));
      }
    } catch (error) {
      console.error('Error deleting note:', error);
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      className="bg-white border border-slate-200 rounded-2xl p-5"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2">
          <Shield className="w-5 h-5 text-amber-500" />
          Internal Notes
        </h3>
        <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-full font-medium">
          Staff Only
        </span>
      </div>

      {/* Add Note Section */}
      <div className="mb-4">
        {isEditing ? (
          <div className="space-y-2">
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Add a note about this client..."
              className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 resize-none"
              rows={3}
              autoFocus
            />
            <div className="flex gap-2">
              <button 
                onClick={handleSaveNote}
                disabled={saving || !noteText.trim()}
                className="flex-1 py-2 bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-1"
              >
                {saving ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {saving ? 'Saving...' : 'Save Note'}
              </button>
              <button 
                onClick={() => { setIsEditing(false); setNoteText(''); }}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-600 text-sm font-medium rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button 
            onClick={() => setIsEditing(true)}
            className="w-full py-3 bg-amber-50 hover:bg-amber-100 border border-amber-200 border-dashed text-amber-700 text-sm font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add New Note
          </button>
        )}
      </div>

      {/* Note History */}
      <div className="space-y-3 max-h-64 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <RefreshCw className="w-5 h-5 text-slate-400 animate-spin" />
          </div>
        ) : noteHistory.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-4">No notes yet</p>
        ) : (
          noteHistory.map((note) => (
            <motion.div 
              key={note.id}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-slate-50 rounded-xl p-3 group"
            >
              <div className="flex items-start justify-between">
                <p className="text-sm text-slate-700 flex-1">{note.text}</p>
                <button 
                  onClick={() => handleDeleteNote(note.id)}
                  className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 rounded text-red-400 hover:text-red-600 transition-all ml-2"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                <span className="font-medium">{note.author_name || 'Staff'}</span>
                <span>•</span>
                <span>{note.created_at ? new Date(note.created_at).toLocaleDateString() : 'Unknown'}</span>
                {note.edited && <span className="text-amber-500">(edited)</span>}
              </div>
            </motion.div>
          ))
        )}
      </div>
    </motion.div>
  );
}

// ============ CLIENT OVERVIEW TAB ============
function ClientOverviewTab({ client, setSubSection }) {
  const [tasks, setTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(true);

  useEffect(() => {
    fetchTasks();
  }, [client.client_id]);

  const fetchTasks = async () => {
    try {
      const res = await fetch(`${API}/api/staff/tasks/${client.client_id}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setTasks(data.tasks || []);
      }
    } catch (err) {
      console.error('Error fetching tasks:', err);
    } finally {
      setLoadingTasks(false);
    }
  };

  // Use client data if available, otherwise show placeholder note
  const clientStats = {
    totalLeads: client.total_leads || '—',
    thisMonth: client.leads_this_month || '—',
    adSpend: client.ad_spend_mtd ? `$${client.ad_spend_mtd.toLocaleString()}` : '—',
    cpl: client.cost_per_lead ? `$${client.cost_per_lead}` : '—',
    conversions: client.conversions || '—',
    activeCampaigns: client.active_campaigns || '—'
  };

  const hasDemoData = !client.total_leads && !client.ad_spend_mtd;

  const recentTasks = tasks
    .filter(t => !t.completed)
    .slice(0, 3)
    .map(t => ({
      id: t.id,
      title: t.title,
      priority: t.priority || 'medium',
      due: t.due_date ? new Date(t.due_date).toLocaleDateString('en-US', { weekday: 'short' }) : 'No date'
    }));

  return (
    <div className="space-y-6">
      {/* Demo Data Notice */}
      {hasDemoData && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
          <p className="text-sm text-amber-700">
            <span className="font-medium">Note:</span> Performance metrics are not yet connected. Connect analytics integrations to see live data.
          </p>
        </div>
      )}

      {/* Quick Stats */}
      <motion.div 
        variants={staggerContainer}
        initial="initial"
        animate="animate"
        className="grid grid-cols-2 lg:grid-cols-6 gap-4"
      >
        {[
          { label: 'Total Leads', value: clientStats.totalLeads, icon: Users, color: 'blue' },
          { label: 'This Month', value: clientStats.thisMonth, icon: TrendingUp, color: 'green' },
          { label: 'Ad Spend (MTD)', value: clientStats.adSpend, icon: DollarSign, color: 'purple' },
          { label: 'Cost Per Lead', value: clientStats.cpl, icon: Target, color: 'amber' },
          { label: 'Conversions', value: clientStats.conversions, icon: CheckCircle2, color: 'green' },
          { label: 'Active Campaigns', value: clientStats.activeCampaigns, icon: Zap, color: 'indigo' },
        ].map((stat, idx) => (
          <motion.div
            key={stat.label}
            variants={fadeInUp}
            whileHover={{ y: -4, boxShadow: '0 10px 40px -10px rgba(0,0,0,0.1)' }}
            className="bg-white border border-slate-200 rounded-xl p-4 cursor-pointer transition-all"
          >
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 bg-${stat.color}-50`}>
              <stat.icon className={`w-4 h-4 text-${stat.color}-500`} />
            </div>
            <p className="text-2xl font-bold text-slate-800">{stat.value}</p>
            <p className="text-xs text-slate-500">{stat.label}</p>
          </motion.div>
        ))}
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Work Queue */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-5"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-blue-500" />
              Work Queue
            </h3>
            <button 
              onClick={() => setSubSection('tasks')}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              View All
            </button>
          </div>
          <div className="space-y-3">
            {loadingTasks ? (
              <div className="flex items-center justify-center py-6">
                <RefreshCw className="w-5 h-5 text-slate-400 animate-spin" />
              </div>
            ) : recentTasks.length === 0 ? (
              <div className="text-center py-6">
                <ClipboardList className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-500 text-sm">No pending tasks</p>
                <button 
                  onClick={() => setSubSection('tasks')}
                  className="text-blue-600 text-sm font-medium mt-2 hover:text-blue-700"
                >
                  Add a task
                </button>
              </div>
            ) : (
              recentTasks.map((task, idx) => (
                <motion.div
                  key={task.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex items-center gap-3 p-3 bg-slate-50 hover:bg-slate-100 rounded-xl transition-colors cursor-pointer group"
                  onClick={() => setSubSection('tasks')}
                >
                  <div className={`w-3 h-3 rounded-full ${
                    task.priority === 'high' ? 'bg-red-500' :
                    task.priority === 'medium' ? 'bg-amber-500' : 'bg-green-500'
                  }`} />
                  <div className="flex-1">
                    <p className="font-medium text-slate-800 group-hover:text-blue-600 transition-colors">
                      {task.title}
                    </p>
                    <p className="text-xs text-slate-500">Due: {task.due}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-blue-500 transition-colors" />
                </motion.div>
              ))
            )}
          </div>
        </motion.div>

        {/* Internal Notes - Staff/Admin Only */}
        <InternalNotesCard client={client} />
      </div>

      {/* Quick Links */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white border border-slate-200 rounded-2xl p-5"
      >
        <h3 className="font-semibold text-slate-800 mb-4">Quick Links & Resources</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {[
            { icon: Globe, label: 'Landing Page', url: client.landing_page_url, color: 'blue' },
            { icon: Target, label: 'Google Ads', url: '#', color: 'green' },
            { icon: Facebook, label: 'Facebook Ads', url: client.facebook_page_url, color: 'indigo' },
            { icon: Instagram, label: 'Instagram', url: '#', color: 'pink' },
            { icon: BarChart3, label: 'Analytics', url: '#', color: 'purple' },
            { icon: FileText, label: 'Invoices', url: '#', color: 'amber' },
          ].map((link, idx) => (
            <motion.a
              key={link.label}
              href={link.url || '#'}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              className={`p-4 bg-${link.color}-50 hover:bg-${link.color}-100 rounded-xl text-center transition-all`}
            >
              <link.icon className={`w-6 h-6 text-${link.color}-500 mx-auto mb-2`} />
              <p className="text-xs font-medium text-slate-700">{link.label}</p>
            </motion.a>
          ))}
        </div>
      </motion.div>

      {/* Contact Info */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="grid grid-cols-1 md:grid-cols-2 gap-6"
      >
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2 mb-4">
            <User className="w-5 h-5 text-blue-500" />
            Primary Contact
          </h3>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center text-white font-semibold">
                {client.primary_contact_first_name?.charAt(0)}{client.primary_contact_last_name?.charAt(0)}
              </div>
              <div>
                <p className="font-medium text-slate-800">
                  {client.primary_contact_first_name} {client.primary_contact_last_name}
                </p>
                <p className="text-xs text-slate-500">Primary Contact</p>
              </div>
            </div>
            <a href={`mailto:${client.primary_contact_email}`} className="flex items-center gap-2 text-sm text-slate-600 hover:text-blue-600">
              <Mail className="w-4 h-4" /> {client.primary_contact_email}
            </a>
            <a href={`tel:${client.primary_contact_phone}`} className="flex items-center gap-2 text-sm text-slate-600 hover:text-blue-600">
              <Phone className="w-4 h-4" /> {client.primary_contact_phone}
            </a>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2 mb-4">
            <Building2 className="w-5 h-5 text-purple-500" />
            Business Info
          </h3>
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Briefcase className="w-4 h-4" /> 
              <span className="font-medium">Industry:</span> {client.industry || 'Not specified'}
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <MapPin className="w-4 h-4" /> 
              {client.business_address || 'No address'}
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Star className="w-4 h-4" /> 
              <span className="font-medium">Plan:</span> {client.plan_tier || 'Growth'}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// ============ CLIENT TASKS TAB ============
function ClientTasksTab({ client }) {
  const [activeTab, setActiveTab] = useState('staff');
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showClientTaskModal, setShowClientTaskModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [loading, setLoading] = useState(true);

  // Staff tasks (internal - client cannot see)
  const [staffTasks, setStaffTasks] = useState([]);

  // Client tasks (visible to client)
  const [clientTasks, setClientTasks] = useState([]);

  // Canned client tasks
  const cannedTasks = [
    { id: 'review_landing', title: 'Review Landing Page', description: 'Please review the updated landing page and provide any feedback or requested changes.' },
    { id: 'approve_ads', title: 'Approve Ad Creative', description: 'Please review and approve the new ad creative designs before we launch the campaign.' },
    { id: 'provide_content', title: 'Provide Content/Photos', description: 'Please provide any additional content, photos, or materials needed for the campaign.' },
    { id: 'review_report', title: 'Review Monthly Report', description: 'Your monthly performance report is ready. Please review and let us know if you have any questions.' },
    { id: 'schedule_call', title: 'Schedule Strategy Call', description: 'Let\'s schedule a call to discuss your campaign strategy and goals.' },
    { id: 'payment_reminder', title: 'Payment Reminder', description: 'This is a friendly reminder that your invoice is due. Please process payment at your earliest convenience.' },
  ];

  // Fetch tasks on mount
  useEffect(() => {
    fetchTasks();
  }, [client.client_id]);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      // Fetch both staff and client tasks in parallel
      const [staffRes, clientRes] = await Promise.all([
        fetch(`${API}/api/staff/tasks/${client.client_id}`, { credentials: 'include' }),
        fetch(`${API}/api/staff/client-tasks/${client.client_id}`, { credentials: 'include' })
      ]);

      if (staffRes.ok) {
        const staffData = await staffRes.json();
        setStaffTasks(staffData.tasks || []);
      }

      if (clientRes.ok) {
        const clientData = await clientRes.json();
        setClientTasks(clientData.tasks || []);
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleStaffTask = async (taskId) => {
    try {
      const response = await fetch(`${API}/api/staff/tasks/${taskId}/toggle`, {
        method: 'PATCH',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setStaffTasks(staffTasks.map(t => t.id === taskId ? data.task : t));
      }
    } catch (error) {
      console.error('Error toggling task:', error);
    }
  };

  const handleSaveStaffTask = async (task) => {
    try {
      if (selectedTask) {
        // Update existing task
        const response = await fetch(`${API}/api/staff/tasks/${task.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            title: task.title,
            description: task.description,
            priority: task.priority,
            due_date: task.due,
            notes: task.notes,
            files: task.files,
            completed: task.completed
          })
        });

        if (response.ok) {
          const data = await response.json();
          setStaffTasks(staffTasks.map(t => t.id === task.id ? data.task : t));
        }
      } else {
        // Create new task
        const response = await fetch(`${API}/api/staff/tasks`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            client_id: client.client_id,
            title: task.title,
            description: task.description,
            priority: task.priority,
            due_date: task.due,
            notes: task.notes,
            files: task.files || []
          })
        });

        if (response.ok) {
          const data = await response.json();
          setStaffTasks([data.task, ...staffTasks]);
        }
      }
    } catch (error) {
      console.error('Error saving task:', error);
    }
    setShowTaskModal(false);
    setSelectedTask(null);
  };

  const handleSendClientTask = async (task) => {
    try {
      const response = await fetch(`${API}/api/staff/client-tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          client_id: client.client_id,
          title: task.title,
          description: task.description,
          due_date: task.due
        })
      });

      if (response.ok) {
        const data = await response.json();
        setClientTasks([data.task, ...clientTasks]);
      }
    } catch (error) {
      console.error('Error sending client task:', error);
    }
    setShowClientTaskModal(false);
  };

  const openTaskDetail = (task) => {
    setSelectedTask(task);
    setShowTaskModal(true);
  };

  return (
    <div className="space-y-6">
      {/* Tab Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex bg-slate-100 rounded-xl p-1">
          <button
            onClick={() => setActiveTab('staff')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'staff' 
                ? 'bg-white text-slate-800 shadow-sm' 
                : 'text-slate-600 hover:text-slate-800'
            }`}
          >
            Staff Tasks ({staffTasks.filter(t => !t.completed).length})
          </button>
          <button
            onClick={() => setActiveTab('client')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'client' 
                ? 'bg-white text-slate-800 shadow-sm' 
                : 'text-slate-600 hover:text-slate-800'
            }`}
          >
            Client Tasks ({clientTasks.filter(t => t.status === 'pending').length})
          </button>
        </div>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => activeTab === 'staff' ? setShowTaskModal(true) : setShowClientTaskModal(true)}
          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-xl transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          {activeTab === 'staff' ? 'Add Task' : 'Send Task to Client'}
        </motion.button>
      </div>

      {/* Staff Tasks View */}
      {activeTab === 'staff' && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="space-y-3"
        >
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-2">
            <Shield className="w-4 h-4 text-amber-600" />
            <p className="text-sm text-amber-800">Staff tasks are internal only - clients cannot see these.</p>
          </div>
          
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
            </div>
          ) : staffTasks.length === 0 ? (
            <div className="text-center py-8 bg-slate-50 rounded-xl">
              <ClipboardList className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="text-slate-500">No staff tasks yet</p>
              <p className="text-sm text-slate-400">Click "Add Task" to create one</p>
            </div>
          ) : (
            staffTasks.map((task, idx) => (
              <motion.div
                key={task.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.05 }}
                onClick={() => openTaskDetail(task)}
                className={`bg-white border border-slate-200 rounded-xl p-4 cursor-pointer hover:shadow-md transition-all ${
                  task.completed ? 'opacity-60' : ''
                }`}
              >
                <div className="flex items-start gap-4">
                  <button 
                    onClick={(e) => { e.stopPropagation(); toggleStaffTask(task.id); }}
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                      task.completed ? 'bg-green-500 border-green-500' : 'border-slate-300 hover:border-blue-500'
                    }`}
                  >
                    {task.completed && <Check className="w-3 h-3 text-white" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className={`font-medium ${task.completed ? 'line-through text-slate-400' : 'text-slate-800'}`}>
                        {task.title}
                      </p>
                      {task.notes && <FileText className="w-3.5 h-3.5 text-slate-400" />}
                    </div>
                    <p className="text-sm text-slate-500 line-clamp-1">{task.description}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-xs text-slate-400 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Due: {task.due_date || task.due}
                      </span>
                    </div>
                  </div>
                  <span className={`px-2.5 py-1 text-xs font-semibold rounded-lg flex-shrink-0 ${
                    task.priority === 'high' ? 'bg-red-50 text-red-600' :
                    task.priority === 'medium' ? 'bg-amber-50 text-amber-600' :
                    'bg-slate-100 text-slate-600'
                  }`}>
                    {task.priority}
                  </span>
                </div>
              </motion.div>
            ))
          )}
        </motion.div>
      )}

      {/* Client Tasks View */}
      {activeTab === 'client' && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="space-y-3"
        >
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-center gap-2">
            <Users className="w-4 h-4 text-blue-600" />
            <p className="text-sm text-blue-800">Client tasks are visible to the client in their dashboard.</p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
            </div>
          ) : clientTasks.length === 0 ? (
            <div className="text-center py-8 bg-slate-50 rounded-xl">
              <ClipboardList className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="text-slate-500">No tasks sent to client yet</p>
              <p className="text-sm text-slate-400">Click "Send Task to Client" to create one</p>
            </div>
          ) : (
            clientTasks.map((task, idx) => (
              <motion.div
                key={task.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="bg-white border border-slate-200 rounded-xl p-4"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium text-slate-800">{task.title}</p>
                      <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                        task.status === 'completed' 
                          ? 'bg-green-50 text-green-600' 
                          : 'bg-amber-50 text-amber-600'
                      }`}>
                        {task.status === 'completed' ? '✓ Completed' : 'Pending'}
                      </span>
                    </div>
                    <p className="text-sm text-slate-500">{task.description}</p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                      <span className="flex items-center gap-1">
                        <Send className="w-3 h-3" />
                        Sent: {task.sent_at ? new Date(task.sent_at).toLocaleDateString() : 'Unknown'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Due: {task.due_date || task.due}
                      </span>
                    </div>
                  </div>
                  <button className="p-2 hover:bg-slate-100 rounded-lg">
                    <MoreVertical className="w-4 h-4 text-slate-400" />
                  </button>
                </div>
              </motion.div>
            ))
          )}
        </motion.div>
      )}

      {/* Staff Task Modal */}
      <AnimatePresence>
        {showTaskModal && (
          <TaskModal 
            task={selectedTask}
            onClose={() => { setShowTaskModal(false); setSelectedTask(null); }}
            onSave={handleSaveStaffTask}
          />
        )}
      </AnimatePresence>

      {/* Send Client Task Modal */}
      <AnimatePresence>
        {showClientTaskModal && (
          <SendClientTaskModal
            cannedTasks={cannedTasks}
            onClose={() => setShowClientTaskModal(false)}
            onSend={handleSendClientTask}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ============ TASK MODAL ============
function TaskModal({ task, onClose, onSave }) {
  const [formData, setFormData] = useState({
    title: task?.title || '',
    description: task?.description || '',
    priority: task?.priority || 'medium',
    due: task?.due_date || task?.due || new Date().toISOString().split('T')[0],
    notes: task?.notes || '',
    files: task?.files || [],
    completed: task?.completed || false
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave({ ...task, ...formData });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <h3 className="font-semibold text-slate-800">{task ? 'Edit Task' : 'New Staff Task'}</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Task Title *</label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              placeholder="What needs to be done?"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              placeholder="Add details about this task..."
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Priority</label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Due Date</label>
              <input
                type="date"
                value={formData.due}
                onChange={(e) => setFormData({ ...formData, due: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Internal Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              placeholder="Add private notes (staff only)..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Attachments</label>
            <div className="border-2 border-dashed border-slate-200 rounded-xl p-4 text-center hover:border-blue-300 transition-colors cursor-pointer">
              <Upload className="w-6 h-6 text-slate-400 mx-auto mb-1" />
              <p className="text-sm text-slate-500">Drop files here or click to upload</p>
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} disabled={saving} className="flex-1 px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl font-medium disabled:opacity-50">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="flex-1 px-4 py-2.5 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white rounded-xl font-medium flex items-center justify-center gap-2">
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? 'Saving...' : task ? 'Save Changes' : 'Create Task'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

// ============ SEND CLIENT TASK MODAL ============
function SendClientTaskModal({ cannedTasks, onClose, onSend }) {
  const [selectedCanned, setSelectedCanned] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    due: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  });

  const handleSelectCanned = (canned) => {
    setSelectedCanned(canned);
    setFormData({ ...formData, title: canned.title, description: canned.description });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSend(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <h3 className="font-semibold text-slate-800">Send Task to Client</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          {/* Canned Tasks */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Quick Select (Canned Tasks)</label>
            <div className="grid grid-cols-2 gap-2">
              {cannedTasks.map((canned) => (
                <button
                  key={canned.id}
                  type="button"
                  onClick={() => handleSelectCanned(canned)}
                  className={`p-3 text-left rounded-xl border-2 transition-all text-sm ${
                    selectedCanned?.id === canned.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-slate-200 hover:border-blue-300'
                  }`}
                >
                  {canned.title}
                </button>
              ))}
            </div>
          </div>

          <div className="border-t border-slate-100 pt-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Task Title *</label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Message to Client *</label>
                <textarea
                  required
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Due Date</label>
                <input
                  type="date"
                  value={formData.due}
                  onChange={(e) => setFormData({ ...formData, due: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl font-medium">
                  Cancel
                </button>
                <button type="submit" className="flex-1 px-4 py-2.5 bg-blue-500 text-white rounded-xl font-medium flex items-center justify-center gap-2">
                  <Send className="w-4 h-4" />
                  Send to Client
                </button>
              </div>
            </form>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// ============ CLIENT APPROVALS TAB (Staff Side) ============
function ClientApprovalsTab({ client }) {
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [filter, setFilter] = useState('all');
  const [selectedApproval, setSelectedApproval] = useState(null);

  useEffect(() => {
    fetchApprovals();
  }, [client.client_id]);

  const fetchApprovals = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/approvals/staff?client_id=${client.client_id}`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setApprovals(data.approvals || []);
      }
    } catch (err) {
      console.error('Error fetching approvals:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (approvalId) => {
    if (!window.confirm('Are you sure you want to delete this approval request?')) return;
    try {
      const res = await fetch(`${API}/api/approvals/${approvalId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (res.ok) {
        setApprovals(approvals.filter(a => a.approval_id !== approvalId));
      }
    } catch (err) {
      console.error('Error deleting approval:', err);
    }
  };

  const filteredApprovals = approvals.filter(a => {
    if (filter === 'pending') return a.status === 'pending';
    if (filter === 'completed') return a.status !== 'pending';
    return true;
  });

  const statusConfig = {
    pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700', icon: Clock },
    approved: { label: 'Approved', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
    rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: X },
    revision_requested: { label: 'Revisions Requested', color: 'bg-purple-100 text-purple-700', icon: RotateCcw }
  };

  const typeConfig = {
    report: { label: 'Report', icon: FileText, color: 'blue' },
    creative: { label: 'Creative', icon: Image, color: 'pink' },
    strategy: { label: 'Strategy', icon: Target, color: 'purple' },
    content: { label: 'Content', icon: FileText, color: 'green' },
    budget: { label: 'Budget', icon: DollarSign, color: 'amber' },
    other: { label: 'Other', icon: File, color: 'slate' }
  };

  const pendingCount = approvals.filter(a => a.status === 'pending').length;

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6"
      data-testid="staff-client-approvals-tab"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-800">Client Approvals</h3>
          <p className="text-sm text-slate-500">Submit items for {client.business_name}'s review and approval</p>
        </div>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setShowSubmitModal(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-xl transition-colors shadow-lg shadow-blue-500/25"
          data-testid="submit-for-approval-btn"
        >
          <SendHorizontal className="w-4 h-4" />
          Submit for Approval
        </motion.button>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-2xl font-bold text-slate-800">{approvals.length}</p>
          <p className="text-xs text-slate-500">Total Submissions</p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <p className="text-2xl font-bold text-amber-600">{pendingCount}</p>
          <p className="text-xs text-amber-600">Awaiting Review</p>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <p className="text-2xl font-bold text-green-600">{approvals.filter(a => a.status === 'approved').length}</p>
          <p className="text-xs text-green-600">Approved</p>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
          <p className="text-2xl font-bold text-purple-600">{approvals.filter(a => a.status === 'revision_requested').length}</p>
          <p className="text-xs text-purple-600">Revisions Needed</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        {[
          { id: 'all', label: 'All' },
          { id: 'pending', label: 'Pending' },
          { id: 'completed', label: 'Completed' }
        ].map(f => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
              filter === f.id
                ? 'bg-blue-500 text-white'
                : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Approvals List */}
      {filteredApprovals.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center">
          <ClipboardCheck className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">
            {filter === 'pending' ? 'No pending approvals' : 'No approvals yet'}
          </h3>
          <p className="text-slate-500 mb-4">
            {filter === 'pending' 
              ? 'All submitted items have been reviewed!'
              : 'Submit work for client approval to get started'}
          </p>
          {filter !== 'pending' && (
            <button
              onClick={() => setShowSubmitModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-xl text-sm font-medium"
            >
              <Plus className="w-4 h-4" />
              Submit First Item
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredApprovals.map(approval => {
            const status = statusConfig[approval.status] || statusConfig.pending;
            const type = typeConfig[approval.approval_type] || typeConfig.other;
            const StatusIcon = status.icon;
            const TypeIcon = type.icon;

            return (
              <motion.div
                key={approval.approval_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`bg-white border-2 rounded-2xl p-5 hover:shadow-lg transition-all cursor-pointer ${
                  approval.status === 'pending' ? 'border-amber-200' :
                  approval.status === 'approved' ? 'border-green-200' :
                  approval.status === 'rejected' ? 'border-red-200' :
                  approval.status === 'revision_requested' ? 'border-purple-200' :
                  'border-slate-200'
                }`}
                onClick={() => setSelectedApproval(approval)}
                data-testid={`approval-item-${approval.approval_id}`}
              >
                <div className="flex items-start gap-4">
                  {/* Type Icon */}
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    type.color === 'blue' ? 'bg-blue-100 text-blue-600' :
                    type.color === 'pink' ? 'bg-pink-100 text-pink-600' :
                    type.color === 'purple' ? 'bg-purple-100 text-purple-600' :
                    type.color === 'green' ? 'bg-green-100 text-green-600' :
                    type.color === 'amber' ? 'bg-amber-100 text-amber-600' :
                    'bg-slate-100 text-slate-600'
                  }`}>
                    <TypeIcon className="w-6 h-6" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h4 className="font-semibold text-slate-800">{approval.title}</h4>
                        <p className="text-sm text-slate-500 line-clamp-2">{approval.description}</p>
                      </div>
                      <span className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium flex-shrink-0 ${status.color}`}>
                        <StatusIcon className="w-3.5 h-3.5" />
                        {status.label}
                      </span>
                    </div>

                    {/* Meta */}
                    <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        Submitted {formatDate(approval.submitted_at)}
                      </span>
                      {approval.attachments?.length > 0 && (
                        <span className="flex items-center gap-1">
                          <Paperclip className="w-3.5 h-3.5" />
                          {approval.attachments.length} file{approval.attachments.length !== 1 ? 's' : ''}
                        </span>
                      )}
                      {approval.response_comment && (
                        <span className="flex items-center gap-1">
                          <MessageSquare className="w-3.5 h-3.5" />
                          Has feedback
                        </span>
                      )}
                    </div>

                    {/* Client Response */}
                    {approval.response_comment && (
                      <div className={`mt-3 p-3 rounded-xl text-sm ${
                        approval.status === 'approved' ? 'bg-green-50 text-green-700' :
                        approval.status === 'rejected' ? 'bg-red-50 text-red-700' :
                        'bg-purple-50 text-purple-700'
                      }`}>
                        <p className="font-medium mb-1">Client Feedback:</p>
                        <p className="text-xs">{approval.response_comment}</p>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {approval.status === 'pending' && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(approval.approval_id); }}
                        className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Cancel Request"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Submit Approval Modal */}
      <AnimatePresence>
        {showSubmitModal && (
          <SubmitApprovalModal
            client={client}
            onClose={() => setShowSubmitModal(false)}
            onSuccess={() => {
              setShowSubmitModal(false);
              fetchApprovals();
            }}
          />
        )}
      </AnimatePresence>

      {/* Approval Detail Modal */}
      <AnimatePresence>
        {selectedApproval && (
          <ApprovalDetailModal
            approval={selectedApproval}
            onClose={() => setSelectedApproval(null)}
            onDelete={() => {
              handleDelete(selectedApproval.approval_id);
              setSelectedApproval(null);
            }}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ============ SUBMIT APPROVAL MODAL ============
function SubmitApprovalModal({ client, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    approval_type: 'creative',
    priority: 'normal',
    due_date: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const approvalTypes = [
    { id: 'creative', label: 'Ad Creative', icon: Image, desc: 'Images, videos, ad designs' },
    { id: 'content', label: 'Content', icon: FileText, desc: 'Copy, articles, captions' },
    { id: 'strategy', label: 'Strategy', icon: Target, desc: 'Marketing plans, proposals' },
    { id: 'report', label: 'Report', icon: BarChart3, desc: 'Performance reports, analytics' },
    { id: 'budget', label: 'Budget', icon: DollarSign, desc: 'Budget changes, ad spend' },
    { id: 'other', label: 'Other', icon: File, desc: 'Other approval requests' }
  ];

  const priorities = [
    { id: 'low', label: 'Low', color: 'slate' },
    { id: 'normal', label: 'Normal', color: 'blue' },
    { id: 'high', label: 'High', color: 'amber' },
    { id: 'urgent', label: 'Urgent', color: 'red' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.description.trim()) {
      setError('Title and description are required');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const res = await fetch(`${API}/api/approvals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          client_id: client.client_id,
          title: formData.title.trim(),
          description: formData.description.trim(),
          approval_type: formData.approval_type,
          priority: formData.priority,
          due_date: formData.due_date || null,
          attachments: []
        })
      });

      if (res.ok) {
        onSuccess();
      } else {
        const data = await res.json();
        setError(data.detail || 'Failed to submit approval request');
      }
    } catch (err) {
      console.error('Error submitting approval:', err);
      setError('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
        data-testid="submit-approval-modal"
      >
        {/* Header */}
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-slate-800">Submit for Client Approval</h3>
              <p className="text-sm text-slate-500">Request approval from {client.business_name}</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg text-slate-400">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Facebook Ad Creative - Holiday Campaign"
              className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              data-testid="approval-title-input"
            />
          </div>

          {/* Approval Type */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Type *</label>
            <div className="grid grid-cols-2 gap-2">
              {approvalTypes.map(type => (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => setFormData({ ...formData, approval_type: type.id })}
                  className={`p-3 rounded-xl border-2 text-left transition-all ${
                    formData.approval_type === type.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <type.icon className={`w-4 h-4 ${
                      formData.approval_type === type.id ? 'text-blue-600' : 'text-slate-400'
                    }`} />
                    <span className={`text-sm font-medium ${
                      formData.approval_type === type.id ? 'text-blue-700' : 'text-slate-700'
                    }`}>{type.label}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{type.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Description *</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe what you're submitting and any specific feedback needed..."
              rows={4}
              className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 resize-none"
              data-testid="approval-description-input"
            />
          </div>

          {/* Priority & Due Date */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Priority</label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              >
                {priorities.map(p => (
                  <option key={p.id} value={p.id}>{p.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Due Date</label>
              <input
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Info Banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-700">
              <p className="font-medium mb-1">What happens next?</p>
              <p className="text-blue-600 text-xs">The client will receive a notification and can approve, reject, or request revisions. You'll be notified of their decision.</p>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200 bg-slate-50 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-xl font-medium hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !formData.title.trim() || !formData.description.trim()}
            className="flex-1 px-4 py-2.5 bg-blue-500 hover:bg-blue-600 disabled:bg-slate-300 text-white rounded-xl font-medium flex items-center justify-center gap-2"
            data-testid="submit-approval-btn"
          >
            {submitting ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <SendHorizontal className="w-4 h-4" />
            )}
            Submit for Approval
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// ============ APPROVAL DETAIL MODAL ============
function ApprovalDetailModal({ approval, onClose, onDelete }) {
  const statusConfig = {
    pending: { label: 'Pending Review', color: 'bg-amber-100 text-amber-700', border: 'border-amber-200' },
    approved: { label: 'Approved', color: 'bg-green-100 text-green-700', border: 'border-green-200' },
    rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', border: 'border-red-200' },
    revision_requested: { label: 'Revisions Requested', color: 'bg-purple-100 text-purple-700', border: 'border-purple-200' }
  };

  const status = statusConfig[approval.status] || statusConfig.pending;

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        onClick={e => e.stopPropagation()}
        className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col shadow-2xl"
      >
        {/* Header */}
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-start justify-between">
            <div>
              <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${status.color} mb-2`}>
                {status.label}
              </span>
              <h3 className="text-lg font-bold text-slate-800">{approval.title}</h3>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg text-slate-400">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Description */}
          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-2">Description</h4>
            <p className="text-slate-600 whitespace-pre-wrap">{approval.description}</p>
          </div>

          {/* Meta */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-slate-500">Submitted By</p>
              <p className="font-medium text-slate-800">{approval.submitted_by_name}</p>
            </div>
            <div>
              <p className="text-slate-500">Submitted On</p>
              <p className="font-medium text-slate-800">{formatDateTime(approval.submitted_at)}</p>
            </div>
            {approval.due_date && (
              <div>
                <p className="text-slate-500">Due Date</p>
                <p className="font-medium text-slate-800">{formatDateTime(approval.due_date)}</p>
              </div>
            )}
            <div>
              <p className="text-slate-500">Type</p>
              <p className="font-medium text-slate-800 capitalize">{approval.approval_type?.replace('_', ' ')}</p>
            </div>
          </div>

          {/* Client Response */}
          {approval.response_comment && (
            <div className={`p-4 rounded-xl border ${status.border} ${status.color.replace('text-', 'bg-').replace('700', '50')}`}>
              <h4 className={`text-sm font-semibold mb-2 ${status.color.split(' ')[1]}`}>
                Client Response
              </h4>
              <p className="text-slate-700 text-sm">{approval.response_comment}</p>
              <p className="text-xs text-slate-500 mt-2">
                Responded {formatDateTime(approval.responded_at)}
              </p>
            </div>
          )}

          {/* Revision History */}
          {approval.revision_history?.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-slate-700 mb-2">History</h4>
              <div className="space-y-2">
                {approval.revision_history.map((entry, idx) => (
                  <div key={idx} className="p-3 bg-slate-50 rounded-lg text-sm">
                    <p className="text-slate-600">
                      <span className="capitalize">{entry.to_status?.replace('_', ' ')}</span>
                      {entry.comment && `: "${entry.comment}"`}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">{formatDateTime(entry.responded_at)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200 bg-slate-50 flex justify-between">
          {approval.status === 'pending' && (
            <button
              onClick={onDelete}
              className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-xl font-medium flex items-center gap-2 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Cancel Request
            </button>
          )}
          <button
            onClick={onClose}
            className="px-6 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-xl font-medium ml-auto"
          >
            Close
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// ============ CLIENT LEADS TAB ============
function ClientLeadsTab({ client, onRefresh }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [viewMode, setViewMode] = useState('pipeline'); // 'pipeline' or 'list'
  const [stats, setStats] = useState({ total: 0, byStage: {} });

  // Pipeline stage configuration
  const STAGES = [
    { id: 'new_lead', label: 'New Lead', color: '#3b82f6' },
    { id: 'attempting_contact', label: 'Attempting Contact', color: '#f59e0b' },
    { id: 'contacted', label: 'Contacted', color: '#8b5cf6' },
    { id: 'site_visit_scheduled', label: 'Site Visit Scheduled', color: '#06b6d4' },
    { id: 'estimate_sent', label: 'Estimate Sent', color: '#ec4899' },
    { id: 'won', label: 'Won', color: '#22c55e' },
    { id: 'lost', label: 'Lost', color: '#ef4444' }
  ];

  useEffect(() => {
    fetchLeads();
  }, [client.client_id]);

  const fetchLeads = async () => {
    try {
      const response = await fetch(`${API}/api/crm/leads?client_id=${client.client_id}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setLeads(data.leads || []);
        
        // Calculate stats
        const byStage = {};
        (data.leads || []).forEach(lead => {
          byStage[lead.pipeline_stage] = (byStage[lead.pipeline_stage] || 0) + 1;
        });
        setStats({ total: data.total || 0, byStage });
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateLeadStage = async (leadId, newStage, lostReason = null) => {
    try {
      const body = { pipeline_stage: newStage };
      if (lostReason) body.lost_reason = lostReason;

      const res = await fetch(`${API}/api/crm/leads/${leadId}/stage`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });

      if (res.ok) {
        fetchLeads();
      }
    } catch (err) {
      console.error('Error updating stage:', err);
    }
  };

  const handleDrop = (e, targetStage) => {
    e.preventDefault();
    const leadId = e.dataTransfer.getData('leadId');
    const currentStage = e.dataTransfer.getData('currentStage');
    
    if (leadId && currentStage !== targetStage) {
      if (targetStage === 'lost') {
        const reason = window.prompt('Please enter a reason for marking this lead as lost:');
        if (reason) updateLeadStage(leadId, targetStage, reason);
      } else {
        updateLeadStage(leadId, targetStage);
      }
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return date.toLocaleDateString();
  };

  const getLeadsByStage = (stageId) => leads.filter(l => l.pipeline_stage === stageId);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-slate-800">Leads for {client.business_name}</h3>
          <p className="text-sm text-slate-500">{stats.total} total leads</p>
        </div>
        <div className="flex gap-2">
          <div className="flex bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('pipeline')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                viewMode === 'pipeline' ? 'bg-white shadow text-slate-800' : 'text-slate-500'
              }`}
            >
              Pipeline
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                viewMode === 'list' ? 'bg-white shadow text-slate-800' : 'text-slate-500'
              }`}
            >
              List
            </button>
          </div>
          <button 
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-xl transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Lead
          </button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {STAGES.slice(0, 5).map(stage => (
          <div 
            key={stage.id}
            className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-xl min-w-fit"
          >
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: stage.color }} />
            <span className="text-sm text-slate-600">{stage.label}</span>
            <span className="text-sm font-bold" style={{ color: stage.color }}>
              {stats.byStage[stage.id] || 0}
            </span>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
        </div>
      ) : leads.length === 0 ? (
        <div className="text-center py-12 bg-white border border-slate-200 rounded-2xl">
          <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="font-semibold text-slate-800 mb-2">No leads yet</h3>
          <p className="text-slate-500 text-sm mb-4">Add leads manually or they'll appear here from your campaigns</p>
          <button 
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-xl"
          >
            Add First Lead
          </button>
        </div>
      ) : viewMode === 'pipeline' ? (
        /* Pipeline View - Constrained to fit screen */
        <div className="overflow-x-auto">
          <div className="flex gap-2 min-w-max pb-2">
            {STAGES.map(stage => {
              const stageLeads = getLeadsByStage(stage.id);
              return (
                <div
                  key={stage.id}
                  className="flex-shrink-0 w-48 bg-slate-50 rounded-xl p-2"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => handleDrop(e, stage.id)}
                >
                  {/* Stage Header */}
                  <div className="flex items-center gap-1.5 mb-2 px-1">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: stage.color }} />
                    <span className="font-medium text-slate-700 text-xs truncate">{stage.label}</span>
                    <span className="ml-auto text-xs font-bold px-1.5 py-0.5 rounded-full" style={{ 
                      backgroundColor: `${stage.color}15`, 
                      color: stage.color 
                    }}>
                      {stageLeads.length}
                    </span>
                  </div>

                  {/* Lead Cards */}
                  <div className="space-y-1.5 max-h-80 overflow-y-auto">
                    {stageLeads.map(lead => (
                      <div
                        key={lead.lead_id}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData('leadId', lead.lead_id);
                          e.dataTransfer.setData('currentStage', lead.pipeline_stage);
                        }}
                        onClick={() => setSelectedLead(lead)}
                        className="bg-white border border-slate-200 rounded-lg p-2 cursor-pointer hover:shadow-md hover:border-blue-300 transition-all"
                      >
                        <p className="font-medium text-slate-800 text-xs truncate">
                          {lead.first_name} {lead.last_name}
                        </p>
                        <p className="text-xs text-slate-500 truncate">{lead.phone}</p>
                        <div className="flex items-center justify-between mt-1">
                          {lead.deal_value ? (
                            <span className="text-xs font-medium text-green-600">
                              ${lead.deal_value.toLocaleString()}
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                          <span className="text-xs text-slate-400">{formatDate(lead.created_at)}</span>
                        </div>
                      </div>
                    ))}
                    {stageLeads.length === 0 && (
                      <p className="text-xs text-slate-400 text-center py-3">No leads</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        /* List View */
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Name</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Contact</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Source</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Stage</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Value</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {leads.map((lead) => {
                const stage = STAGES.find(s => s.id === lead.pipeline_stage) || STAGES[0];
                return (
                  <tr 
                    key={lead.lead_id} 
                    className="hover:bg-slate-50 cursor-pointer"
                    onClick={() => setSelectedLead(lead)}
                  >
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-800">{lead.first_name} {lead.last_name}</p>
                      {lead.service_type && (
                        <p className="text-xs text-slate-500">{lead.service_type}</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-slate-600">{lead.phone}</p>
                      {lead.email && <p className="text-xs text-slate-400">{lead.email}</p>}
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-blue-50 text-blue-600 text-xs rounded-lg capitalize">
                        {(lead.source || 'unknown').replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span 
                        className="px-2 py-1 text-xs rounded-lg font-medium"
                        style={{ backgroundColor: `${stage.color}15`, color: stage.color }}
                      >
                        {stage.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {lead.deal_value ? `$${lead.deal_value.toLocaleString()}` : '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500">
                      {formatDate(lead.created_at)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Lead Modal */}
      {showAddModal && (
        <ClientLeadAddModal 
          client={client} 
          onClose={() => setShowAddModal(false)} 
          onSuccess={() => { fetchLeads(); setShowAddModal(false); }}
        />
      )}

      {/* Lead Detail Modal */}
      {selectedLead && (
        <ClientLeadDetailModal
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          onUpdate={fetchLeads}
          stages={STAGES}
        />
      )}
    </div>
  );
}

// ============ CLIENT LEAD ADD MODAL ============
function ClientLeadAddModal({ client, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    source: 'website_form',
    service_type: '',
    deal_value: '',
    address: '',
    city: '',
    state: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await fetch(`${API}/api/crm/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          client_id: client.client_id,
          deal_value: formData.deal_value ? parseFloat(formData.deal_value) : null
        })
      });
      if (response.ok) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error adding lead:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <h3 className="font-semibold text-slate-800">Add New Lead</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">First Name *</label>
              <input
                type="text"
                required
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Last Name</label>
              <input
                type="text"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Phone *</label>
              <input
                type="tel"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Source</label>
              <select
                value={formData.source}
                onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              >
                <option value="google_ads">Google Ads</option>
                <option value="facebook_ads">Facebook Ads</option>
                <option value="instagram_ads">Instagram Ads</option>
                <option value="website_form">Website Form</option>
                <option value="phone_call">Phone Call</option>
                <option value="referral">Referral</option>
                <option value="direct">Direct</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Deal Value ($)</label>
              <input
                type="number"
                value={formData.deal_value}
                onChange={(e) => setFormData({ ...formData, deal_value: e.target.value })}
                placeholder="0"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Service Type</label>
            <input
              type="text"
              value={formData.service_type}
              onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
              placeholder="e.g., Roof Repair, Kitchen Remodel"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            />
          </div>
          <div className="pt-4 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-200 text-slate-600 rounded-xl hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !formData.first_name || !formData.phone}
              className="flex-1 px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-slate-300 text-white rounded-xl flex items-center justify-center gap-2"
            >
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Add Lead
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

// ============ CLIENT LEAD DETAIL MODAL ============
function ClientLeadDetailModal({ lead, onClose, onUpdate, stages }) {
  const [activeTab, setActiveTab] = useState('details');
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [fullLead, setFullLead] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeadDetails();
  }, [lead.lead_id]);

  const fetchLeadDetails = async () => {
    try {
      const res = await fetch(`${API}/api/crm/leads/${lead.lead_id}`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setFullLead(data.lead);
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    setAddingNote(true);
    try {
      const res = await fetch(`${API}/api/crm/leads/${lead.lead_id}/notes?note_text=${encodeURIComponent(newNote)}`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        setNewNote('');
        fetchLeadDetails();
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setAddingNote(false);
    }
  };

  const sendToContractorOS = async () => {
    try {
      const res = await fetch(`${API}/api/crm/leads/${lead.lead_id}/send-to-contractor-os`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        alert('Lead marked for ContractorOS sync!');
        fetchLeadDetails();
        onUpdate();
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const leadData = fullLead || lead;
  const currentStage = stages.find(s => s.id === leadData.pipeline_stage) || stages[0];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-800">
              {leadData.first_name} {leadData.last_name}
            </h2>
            <div className="flex items-center gap-2 mt-1">
              <span 
                className="px-2 py-0.5 text-xs font-medium rounded-full"
                style={{ backgroundColor: `${currentStage.color}15`, color: currentStage.color }}
              >
                {currentStage.label}
              </span>
              {leadData.service_type && (
                <span className="text-sm text-slate-500">{leadData.service_type}</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!leadData.contractor_os_synced && (
              <button
                onClick={sendToContractorOS}
                className="px-3 py-1.5 bg-purple-500 hover:bg-purple-600 text-white text-xs rounded-lg flex items-center gap-1"
              >
                <Send className="w-3 h-3" />
                Send to ContractorOS
              </button>
            )}
            {leadData.contractor_os_synced && (
              <span className="px-3 py-1.5 bg-green-100 text-green-700 text-xs rounded-lg flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" />
                Synced
              </span>
            )}
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 140px)' }}>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-slate-800 mb-3">Contact</h3>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <span>{leadData.phone}</span>
                    </div>
                    {leadData.email && (
                      <div className="flex items-center gap-2 text-sm">
                        <Mail className="w-4 h-4 text-slate-400" />
                        <span>{leadData.email}</span>
                      </div>
                    )}
                    {leadData.address && (
                      <div className="flex items-center gap-2 text-sm">
                        <MapPin className="w-4 h-4 text-slate-400" />
                        <span>{leadData.address}{leadData.city && `, ${leadData.city}`}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800 mb-3">Details</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Source</span>
                      <span className="capitalize">{(leadData.source || 'unknown').replace(/_/g, ' ')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Value</span>
                      <span className="font-medium text-green-600">
                        {leadData.deal_value ? `$${leadData.deal_value.toLocaleString()}` : '—'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Created</span>
                      <span>{new Date(leadData.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <h3 className="font-semibold text-slate-800 mb-3">Notes</h3>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Add a note..."
                    className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    onKeyPress={(e) => e.key === 'Enter' && addNote()}
                  />
                  <button
                    onClick={addNote}
                    disabled={addingNote || !newNote.trim()}
                    className="px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-slate-300 text-white text-sm rounded-lg"
                  >
                    {addingNote ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Add'}
                  </button>
                </div>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {/* Handle notes as array */}
                  {Array.isArray(leadData.notes) && leadData.notes.map((note, idx) => (
                    <div key={idx} className="bg-slate-50 rounded-lg p-3">
                      <p className="text-sm">{typeof note === 'string' ? note : note.text}</p>
                      {typeof note === 'object' && note.created_at && (
                        <p className="text-xs text-slate-400 mt-1">
                          {note.created_by_name} • {new Date(note.created_at).toLocaleString()}
                        </p>
                      )}
                    </div>
                  ))}
                  {/* Handle notes as string */}
                  {typeof leadData.notes === 'string' && leadData.notes.trim() && (
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-sm">{leadData.notes}</p>
                    </div>
                  )}
                  {(!leadData.notes || (Array.isArray(leadData.notes) && leadData.notes.length === 0) || (typeof leadData.notes === 'string' && !leadData.notes.trim())) && (
                    <p className="text-sm text-slate-400 text-center py-4">No notes yet</p>
                  )}
                </div>
              </div>

              {/* Activity */}
              <div>
                <h3 className="font-semibold text-slate-800 mb-3">Activity</h3>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {(leadData.activities || []).slice(0, 10).map((activity, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-sm">
                      <div className="w-6 h-6 bg-slate-100 rounded flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Clock className="w-3 h-3 text-slate-500" />
                      </div>
                      <div>
                        <p className="text-slate-700">{activity.description}</p>
                        <p className="text-xs text-slate-400">{new Date(activity.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                  {(!leadData.activities || leadData.activities.length === 0) && (
                    <p className="text-sm text-slate-400 text-center py-4">No activity yet</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}

// ============ CLIENT ADS TAB ============
function ClientAdsTab({ client }) {
  const campaigns = [
    { id: 1, name: 'Summer Promo 2024', platform: 'Google Ads', status: 'active', spend: 1847, leads: 28, cpl: 65.96 },
    { id: 2, name: 'Brand Awareness', platform: 'Facebook', status: 'active', spend: 2890, leads: 42, cpl: 68.81 },
    { id: 3, name: 'Holiday Special', platform: 'Instagram', status: 'paused', spend: 1200, leads: 15, cpl: 80.00 },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-800">Ad Campaigns</h3>
        <div className="flex gap-2">
          <button className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium rounded-xl flex items-center gap-2">
            <ExternalLink className="w-4 h-4" />
            Open Google Ads
          </button>
          <button className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium rounded-xl flex items-center gap-2">
            <ExternalLink className="w-4 h-4" />
            Open Meta Ads
          </button>
        </div>
      </div>

      <div className="grid gap-4">
        {campaigns.map((campaign) => (
          <div key={campaign.id} className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  campaign.platform === 'Google Ads' ? 'bg-blue-50' :
                  campaign.platform === 'Facebook' ? 'bg-indigo-50' : 'bg-pink-50'
                }`}>
                  {campaign.platform === 'Google Ads' ? <Globe className="w-5 h-5 text-blue-500" /> :
                   campaign.platform === 'Facebook' ? <Facebook className="w-5 h-5 text-indigo-500" /> :
                   <Instagram className="w-5 h-5 text-pink-500" />}
                </div>
                <div>
                  <h4 className="font-medium text-slate-800">{campaign.name}</h4>
                  <p className="text-xs text-slate-500">{campaign.platform}</p>
                </div>
              </div>
              <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${
                campaign.status === 'active' ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-600'
              }`}>
                {campaign.status}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-4 pt-3 border-t border-slate-100">
              <div>
                <p className="text-xs text-slate-500">Spend</p>
                <p className="font-semibold text-slate-800">${campaign.spend}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Leads</p>
                <p className="font-semibold text-slate-800">{campaign.leads}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">CPL</p>
                <p className={`font-semibold ${campaign.cpl < 70 ? 'text-green-600' : 'text-amber-600'}`}>
                  ${campaign.cpl}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============ CLIENT FILES TAB ============
function ClientFilesTab({ client }) {
  const [files, setFiles] = useState(client?.assets || []);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setFiles(client?.assets || []);
  }, [client]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('asset_type', 'other');

    try {
      const res = await fetch(`${API}/api/staff/assets/upload/${client.client_id}`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        setFiles(prev => [...prev, data.asset]);
      }
    } catch (err) {
      console.error('Error uploading file:', err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (assetId) => {
    if (!confirm('Are you sure you want to delete this file?')) return;

    setDeleting(assetId);
    try {
      const res = await fetch(`${API}/api/staff/assets/${client.client_id}/${assetId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (res.ok) {
        setFiles(prev => prev.filter(f => f.asset_id !== assetId));
      }
    } catch (err) {
      console.error('Error deleting file:', err);
    } finally {
      setDeleting(null);
    }
  };

  const getFileIcon = (ext) => {
    if (['.jpg', '.jpeg', '.png', '.gif', '.webp'].includes(ext)) return Image;
    if (ext === '.pdf') return FileText;
    return File;
  };

  return (
    <div className="space-y-4" data-testid="client-files-tab">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-800">Files for {client.business_name}</h3>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleUpload}
            className="hidden"
            accept=".png,.jpg,.jpeg,.pdf,.doc,.docx,.xls,.xlsx,.zip,.mp4"
          />
          <button 
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-slate-300 text-white text-sm font-medium rounded-xl flex items-center gap-2"
          >
            {uploading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Upload File
          </button>
        </div>
      </div>

      {files.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center">
          <FolderOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h4 className="font-medium text-slate-700 mb-2">No files yet</h4>
          <p className="text-slate-500 text-sm mb-4">Upload files for this client to share with them</p>
          <button 
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-xl text-sm font-medium"
          >
            <Upload className="w-4 h-4" />
            Upload First File
          </button>
        </div>
      ) : (
        <div className="grid gap-3">
          {files.map((file) => {
            const FileIconComponent = getFileIcon(file.file_extension);
            return (
              <div 
                key={file.asset_id} 
                className="bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between group hover:shadow-md transition-all"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                    <FileIconComponent className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">{file.name}</p>
                    <p className="text-xs text-slate-500">
                      By {file.uploaded_by_name} • {new Date(file.uploaded_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={`${API}${file.file_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <Download className="w-4 h-4 text-slate-500" />
                  </a>
                  <button 
                    onClick={() => handleDelete(file.asset_id)}
                    disabled={deleting === file.asset_id}
                    className="p-2 hover:bg-red-50 rounded-lg text-slate-400 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                  >
                    {deleting === file.asset_id ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============ CLIENT MESSAGES TAB ============
function ClientMessagesTab({ client }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);

  useEffect(() => {
    fetchMessages();
    // Poll for new messages every 5 seconds
    pollIntervalRef.current = setInterval(fetchMessages, 5000);
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [client.client_id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchMessages = async () => {
    try {
      const res = await fetch(`${API}/api/staff/messages/${client.client_id}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const res = await fetch(`${API}/api/staff/messages/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          client_id: client.client_id,
          message_text: newMessage
        })
      });

      if (res.ok) {
        setNewMessage('');
        fetchMessages();
      }
    } catch (err) {
      console.error('Error sending message:', err);
    } finally {
      setSending(false);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden flex flex-col" style={{ height: 'calc(100vh - 380px)', minHeight: '400px' }} data-testid="client-messages-tab">
      {/* Chat Header */}
      <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-3 bg-slate-50">
        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center text-white font-semibold">
          {client.business_name?.charAt(0) || 'C'}
        </div>
        <div>
          <p className="font-medium text-slate-800 text-sm">{client.business_name}</p>
          <p className="text-xs text-slate-500">
            {client.primary_contact_first_name} {client.primary_contact_last_name}
          </p>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50/50">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <MessageSquare className="w-12 h-12 text-slate-300 mb-3" />
            <h4 className="font-medium text-slate-700 mb-1">No messages yet</h4>
            <p className="text-slate-500 text-sm">Start a conversation with {client.business_name}</p>
          </div>
        ) : (
          messages.map((msg) => (
            <div
              key={msg.message_id}
              className={`flex ${msg.sender_type === 'staff' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl ${
                msg.sender_type === 'staff'
                  ? 'bg-blue-500 text-white rounded-br-md'
                  : 'bg-white text-slate-700 border border-slate-200 rounded-bl-md shadow-sm'
              }`}>
                {msg.sender_type !== 'staff' && (
                  <p className="text-xs font-medium text-slate-500 mb-1">{msg.sender_name}</p>
                )}
                <p className="text-sm">{msg.message_text}</p>
                <p className={`text-xs mt-1 ${msg.sender_type === 'staff' ? 'text-blue-100' : 'text-slate-400'}`}>
                  {formatTime(msg.timestamp)}
                </p>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <form onSubmit={sendMessage} className="p-4 border-t border-slate-200 bg-white">
        <div className="flex items-center gap-3">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type your message..."
            className="flex-1 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
          />
          <button
            type="submit"
            disabled={!newMessage.trim() || sending}
            className="p-2.5 bg-blue-500 hover:bg-blue-600 disabled:bg-slate-200 text-white rounded-xl transition-colors"
          >
            {sending ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
          </button>
        </div>
      </form>
    </div>
  );
}

// ============ CLIENT SETTINGS TAB ============
function ClientSettingsTab({ client, onRefresh }) {
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    business_name: client.business_name || '',
    primary_contact_email: client.primary_contact_email || '',
    primary_contact_phone: client.primary_contact_phone || '',
    plan_tier: client.plan_tier || 'growth',
    monthly_fee: client.monthly_fee || 0,
  });

  return (
    <div className="space-y-6">
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-800">Client Information</h3>
          <button 
            onClick={() => setEditing(!editing)}
            className="px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg flex items-center gap-1"
          >
            <Edit3 className="w-4 h-4" />
            {editing ? 'Cancel' : 'Edit'}
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Business Name</label>
            <input
              type="text"
              value={formData.business_name}
              onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
              disabled={!editing}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm disabled:bg-slate-50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
            <input
              type="email"
              value={formData.primary_contact_email}
              onChange={(e) => setFormData({ ...formData, primary_contact_email: e.target.value })}
              disabled={!editing}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm disabled:bg-slate-50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
            <input
              type="tel"
              value={formData.primary_contact_phone}
              onChange={(e) => setFormData({ ...formData, primary_contact_phone: e.target.value })}
              disabled={!editing}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm disabled:bg-slate-50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Plan Tier</label>
            <select
              value={formData.plan_tier}
              onChange={(e) => setFormData({ ...formData, plan_tier: e.target.value })}
              disabled={!editing}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm disabled:bg-slate-50"
            >
              <option value="starter">Starter</option>
              <option value="growth">Growth</option>
              <option value="scale">Scale</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>
        </div>

        {editing && (
          <div className="flex gap-3 mt-4 pt-4 border-t border-slate-100">
            <button 
              onClick={() => setEditing(false)}
              className="px-4 py-2 bg-slate-100 text-slate-700 rounded-xl text-sm font-medium"
            >
              Cancel
            </button>
            <button className="px-4 py-2 bg-blue-500 text-white rounded-xl text-sm font-medium flex items-center gap-2">
              <Save className="w-4 h-4" />
              Save Changes
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ============ AGENCY LEADS VIEW ============
function AgencyLeadsView({ leads, onRefresh }) {
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);

  const filteredLeads = leads.filter(lead => 
    statusFilter === 'all' || lead.status === statusFilter
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-6"
    >
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Agency Leads</h2>
          <p className="text-slate-500 text-sm">Potential new clients for BirdenMedia</p>
        </div>
        <button 
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-xl flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Lead
        </button>
      </div>

      {/* Status Filters */}
      <div className="flex gap-2 overflow-x-auto">
        {['all', 'new', 'contacted', 'qualified', 'converted'].map((status) => (
          <button
            key={status}
            onClick={() => setStatusFilter(status)}
            className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
              statusFilter === status
                ? 'bg-blue-500 text-white'
                : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {/* Leads Table */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Contact</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Company</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Source</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Status</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Date</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredLeads.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-slate-500">
                  No leads found
                </td>
              </tr>
            ) : (
              filteredLeads.map((lead) => (
                <tr key={lead.lead_id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-slate-800">{lead.first_name} {lead.last_name}</p>
                    <p className="text-xs text-slate-500">{lead.email}</p>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{lead.company_name || '-'}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-lg">
                      {lead.source || 'Website'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-lg ${
                      lead.status === 'new' ? 'bg-blue-50 text-blue-600' :
                      lead.status === 'contacted' ? 'bg-amber-50 text-amber-600' :
                      lead.status === 'qualified' ? 'bg-green-50 text-green-600' :
                      'bg-purple-50 text-purple-600'
                    }`}>
                      {lead.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-500">
                    {new Date(lead.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    <button className="p-2 hover:bg-slate-100 rounded-lg">
                      <MoreVertical className="w-4 h-4 text-slate-400" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </motion.div>
  );
}

// ============ AGENCY APPOINTMENTS VIEW ============
function AgencyAppointmentsView() {
  const appointments = [
    { id: 1, title: 'Discovery Call - ABC Corp', date: '2024-12-23', time: '10:00 AM', type: 'sales' },
    { id: 2, title: 'Strategy Review - XYZ Inc', date: '2024-12-23', time: '2:00 PM', type: 'client' },
    { id: 3, title: 'New Lead Follow-up', date: '2024-12-24', time: '11:00 AM', type: 'sales' },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-6"
    >
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Appointments</h2>
          <p className="text-slate-500 text-sm">Schedule and manage appointments</p>
        </div>
        <button className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-xl flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Schedule Appointment
        </button>
      </div>

      <div className="grid gap-4">
        {appointments.map((apt) => (
          <div key={apt.id} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                apt.type === 'sales' ? 'bg-purple-50' : 'bg-blue-50'
              }`}>
                <CalendarDays className={`w-6 h-6 ${apt.type === 'sales' ? 'text-purple-500' : 'text-blue-500'}`} />
              </div>
              <div>
                <h4 className="font-medium text-slate-800">{apt.title}</h4>
                <p className="text-sm text-slate-500">{apt.date} at {apt.time}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${
                apt.type === 'sales' ? 'bg-purple-50 text-purple-600' : 'bg-blue-50 text-blue-600'
              }`}>
                {apt.type}
              </span>
              <button className="p-2 hover:bg-slate-100 rounded-lg">
                <MoreVertical className="w-4 h-4 text-slate-400" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

// ============ STAFF MESSAGES VIEW ============
function StaffMessagesView({ clients }) {
  const [conversations, setConversations] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    if (selectedClient) {
      fetchMessages(selectedClient.client_id);
    }
  }, [selectedClient]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchConversations = async () => {
    try {
      const res = await fetch(`${API}/api/staff/messages/inbox/all`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setConversations(data.conversations || []);
      }
    } catch (err) {
      console.error('Error fetching conversations:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (clientId) => {
    setLoadingMessages(true);
    try {
      const res = await fetch(`${API}/api/staff/messages/${clientId}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
        // Mark as read
        await fetch(`${API}/api/staff/messages/mark-read/${clientId}`, { 
          method: 'PATCH', 
          credentials: 'include' 
        });
        fetchConversations(); // Refresh unread counts
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
    } finally {
      setLoadingMessages(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedClient || sending) return;

    setSending(true);
    try {
      const res = await fetch(`${API}/api/staff/messages/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          client_id: selectedClient.client_id,
          message_text: newMessage
        })
      });

      if (res.ok) {
        setNewMessage('');
        fetchMessages(selectedClient.client_id);
      }
    } catch (err) {
      console.error('Error sending message:', err);
    } finally {
      setSending(false);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return date.toLocaleDateString();
  };

  const filteredConversations = conversations.filter(c =>
    c.business_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.primary_contact_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Also build conversations from clients that have no messages yet
  const clientsWithoutConvo = clients.filter(
    c => !conversations.find(conv => conv.client_id === c.client_id)
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-6"
      data-testid="staff-messages-view"
    >
      <div>
        <h2 className="text-xl font-bold text-slate-800">Messages</h2>
        <p className="text-slate-500 text-sm">All client conversations</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" style={{ height: 'calc(100vh - 250px)', minHeight: '500px' }}>
        {/* Conversations List */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden flex flex-col">
          <div className="p-4 border-b border-slate-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm"
              />
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <RefreshCw className="w-5 h-5 text-slate-400 animate-spin" />
              </div>
            ) : filteredConversations.length === 0 && clientsWithoutConvo.length === 0 ? (
              <div className="p-8 text-center">
                <MessageSquare className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">No conversations yet</p>
              </div>
            ) : (
              <>
                {filteredConversations.map((conv) => (
                  <button
                    key={conv.client_id}
                    onClick={() => setSelectedClient(conv)}
                    className={`w-full p-4 border-b border-slate-100 text-left hover:bg-slate-50 transition-colors ${
                      selectedClient?.client_id === conv.client_id ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                        {conv.business_name?.charAt(0) || 'C'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-slate-800 truncate">{conv.business_name}</p>
                          {conv.unread_count > 0 && (
                            <span className="w-5 h-5 bg-blue-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                              {conv.unread_count}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 truncate">{conv.last_message}</p>
                        <p className="text-xs text-slate-400 mt-1">{formatTime(conv.last_message_time)}</p>
                      </div>
                    </div>
                  </button>
                ))}
                {/* Show clients without conversations */}
                {clientsWithoutConvo.filter(c => 
                  c.business_name?.toLowerCase().includes(searchQuery.toLowerCase())
                ).map((client) => (
                  <button
                    key={client.client_id}
                    onClick={() => setSelectedClient({ 
                      client_id: client.client_id, 
                      business_name: client.business_name,
                      primary_contact_name: `${client.primary_contact_first_name || ''} ${client.primary_contact_last_name || ''}`.trim()
                    })}
                    className={`w-full p-4 border-b border-slate-100 text-left hover:bg-slate-50 transition-colors ${
                      selectedClient?.client_id === client.client_id ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-slate-200 rounded-xl flex items-center justify-center text-slate-600 font-semibold text-sm flex-shrink-0">
                        {client.business_name?.charAt(0) || 'C'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-800 truncate">{client.business_name}</p>
                        <p className="text-xs text-slate-400">No messages yet</p>
                      </div>
                    </div>
                  </button>
                ))}
              </>
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl overflow-hidden flex flex-col">
          {selectedClient ? (
            <>
              {/* Chat Header */}
              <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-3 bg-slate-50">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center text-white font-semibold">
                  {selectedClient.business_name?.charAt(0) || 'C'}
                </div>
                <div>
                  <p className="font-medium text-slate-800">{selectedClient.business_name}</p>
                  <p className="text-xs text-slate-500">{selectedClient.primary_contact_name}</p>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50/50">
                {loadingMessages ? (
                  <div className="flex items-center justify-center h-full">
                    <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center">
                    <MessageSquare className="w-12 h-12 text-slate-300 mb-3" />
                    <p className="text-slate-500">No messages yet. Start the conversation!</p>
                  </div>
                ) : (
                  messages.map((msg) => (
                    <div
                      key={msg.message_id}
                      className={`flex ${msg.sender_type === 'staff' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[70%] px-4 py-2.5 rounded-2xl ${
                        msg.sender_type === 'staff'
                          ? 'bg-blue-500 text-white rounded-br-md'
                          : 'bg-white text-slate-700 border border-slate-200 rounded-bl-md'
                      }`}>
                        {msg.sender_type !== 'staff' && (
                          <p className="text-xs font-medium mb-1 opacity-70">{msg.sender_name}</p>
                        )}
                        <p className="text-sm">{msg.message_text}</p>
                        <p className={`text-xs mt-1 ${msg.sender_type === 'staff' ? 'text-blue-100' : 'text-slate-400'}`}>
                          {formatTime(msg.timestamp)}
                        </p>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <form onSubmit={sendMessage} className="p-4 border-t border-slate-200 bg-white">
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type your message..."
                    className="flex-1 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm"
                  />
                  <button
                    type="submit"
                    disabled={!newMessage.trim() || sending}
                    className="p-2.5 bg-blue-500 hover:bg-blue-600 disabled:bg-slate-200 text-white rounded-xl transition-colors"
                  >
                    {sending ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                  </button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageSquare className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="font-semibold text-slate-800 mb-2">Select a Conversation</h3>
                <p className="text-slate-500 text-sm">Choose a client from the list to view messages</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

// ============ STAFF FILES VIEW ============
function StaffFilesView({ clients }) {
  const [selectedClient, setSelectedClient] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (selectedClient) {
      fetchClientFiles(selectedClient.client_id);
    }
  }, [selectedClient]);

  const fetchClientFiles = async (clientId) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/staff/clients`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const client = data.clients?.find(c => c.client_id === clientId);
        setFiles(client?.assets || []);
      }
    } catch (err) {
      console.error('Error fetching files:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedClient) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('asset_type', 'other');

    try {
      const res = await fetch(`${API}/api/staff/assets/upload/${selectedClient.client_id}`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (res.ok) {
        fetchClientFiles(selectedClient.client_id);
      }
    } catch (err) {
      console.error('Error uploading file:', err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (assetId) => {
    if (!selectedClient || !confirm('Are you sure you want to delete this file?')) return;

    try {
      const res = await fetch(`${API}/api/staff/assets/${selectedClient.client_id}/${assetId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (res.ok) {
        fetchClientFiles(selectedClient.client_id);
      }
    } catch (err) {
      console.error('Error deleting file:', err);
    }
  };

  const getFileIcon = (ext) => {
    if (['.jpg', '.jpeg', '.png', '.gif', '.webp'].includes(ext)) return Image;
    if (ext === '.pdf') return FileText;
    return File;
  };

  const filteredClients = clients.filter(c =>
    c.business_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-6"
      data-testid="staff-files-view"
    >
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Files</h2>
          <p className="text-slate-500 text-sm">Manage documents for all clients</p>
        </div>
        {selectedClient && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleUpload}
              className="hidden"
              accept=".png,.jpg,.jpeg,.pdf,.doc,.docx,.xls,.xlsx,.zip,.mp4"
            />
            <button 
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-slate-300 text-white text-sm font-medium rounded-xl flex items-center gap-2"
            >
              {uploading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Upload File
            </button>
          </>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Client List */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-slate-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search clients..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm"
              />
            </div>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {filteredClients.map((client) => (
              <button
                key={client.client_id}
                onClick={() => setSelectedClient(client)}
                className={`w-full p-4 border-b border-slate-100 text-left hover:bg-slate-50 transition-colors ${
                  selectedClient?.client_id === client.client_id ? 'bg-blue-50' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center text-white font-semibold text-xs">
                    {client.business_name?.charAt(0) || 'C'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-800 text-sm truncate">{client.business_name}</p>
                    <p className="text-xs text-slate-500">{client.assets?.length || 0} files</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Files Grid */}
        <div className="lg:col-span-3">
          {selectedClient ? (
            <div className="bg-white border border-slate-200 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-800">Files for {selectedClient.business_name}</h3>
                <span className="text-sm text-slate-500">{files.length} file{files.length !== 1 ? 's' : ''}</span>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
                </div>
              ) : files.length === 0 ? (
                <div className="text-center py-12">
                  <FolderOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h4 className="font-medium text-slate-700 mb-2">No files yet</h4>
                  <p className="text-slate-500 text-sm mb-4">Upload files for this client</p>
                  <button 
                    onClick={() => fileInputRef.current?.click()}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-xl text-sm font-medium"
                  >
                    <Upload className="w-4 h-4" />
                    Upload First File
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {files.map((file) => {
                    const FileIcon = getFileIcon(file.file_extension);
                    return (
                      <motion.div
                        key={file.asset_id}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-slate-50 border border-slate-200 rounded-xl p-4 group hover:shadow-md transition-all"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                            <FileIcon className="w-6 h-6 text-blue-600" />
                          </div>
                          <button
                            onClick={() => handleDelete(file.asset_id)}
                            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <h4 className="text-sm font-medium text-slate-800 truncate mb-1">{file.name}</h4>
                        <p className="text-xs text-slate-500 mb-3">
                          By {file.uploaded_by_name} • {new Date(file.uploaded_at).toLocaleDateString()}
                        </p>
                        <a
                          href={`${API}${file.file_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 font-medium"
                        >
                          <Download className="w-3.5 h-3.5" />
                          Download
                        </a>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center">
              <FolderOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="font-semibold text-slate-800 mb-2">Select a Client</h3>
              <p className="text-slate-500 text-sm">Choose a client from the list to view and manage their files</p>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

// ============ AGENCY SOP VIEW ============
function AgencySOPView() {
  const [sops, setSops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [selectedSop, setSelectedSop] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const categories = ['All', 'Onboarding', 'Campaigns', 'Communication', 'Development', 'Reporting'];

  const iconMap = {
    'Rocket': Rocket,
    'Target': Target,
    'Facebook': Facebook,
    'MessageSquare': MessageSquare,
    'Globe': Globe,
    'BarChart': BarChart3,
    'FileText': FileText
  };

  useEffect(() => {
    fetchSops();
  }, [activeCategory, searchQuery]);

  const fetchSops = async () => {
    setLoading(true);
    try {
      let url = `${API}/api/sops`;
      const params = new URLSearchParams();
      if (activeCategory !== 'All') params.append('category', activeCategory);
      if (searchQuery) params.append('search', searchQuery);
      if (params.toString()) url += `?${params.toString()}`;

      console.log('[SOPs] Fetching from:', url);
      const res = await fetch(url, { credentials: 'include' });
      console.log('[SOPs] Response status:', res.status);
      
      if (res.ok) {
        const data = await res.json();
        console.log('[SOPs] Received:', data.sops?.length || 0, 'SOPs');
        setSops(data.sops || []);
      } else {
        const errText = await res.text();
        console.error('[SOPs] API Error:', res.status, errText);
      }
    } catch (err) {
      console.error('[SOPs] Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    if (days < 30) return `${Math.floor(days / 7)} week${Math.floor(days / 7) > 1 ? 's' : ''} ago`;
    return `${Math.floor(days / 30)} month${Math.floor(days / 30) > 1 ? 's' : ''} ago`;
  };

  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    indigo: 'bg-indigo-100 text-indigo-600',
    purple: 'bg-purple-100 text-purple-600',
    amber: 'bg-amber-100 text-amber-600',
    pink: 'bg-pink-100 text-pink-600'
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-blue-500" />
            SOPs & Guides
          </h2>
          <p className="text-slate-500 text-sm">Standard operating procedures and how-to guides</p>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search SOPs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-sm w-64"
          />
        </div>
      </div>

      {/* Category Filters */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {categories.map((cat) => (
          <motion.button
            key={cat}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setActiveCategory(cat)}
            className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
              activeCategory === cat
                ? 'bg-blue-500 text-white'
                : 'bg-white border border-slate-200 text-slate-600 hover:border-blue-300'
            }`}
          >
            {cat}
          </motion.button>
        ))}
      </div>

      {/* SOP Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
        </div>
      ) : sops.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-slate-200">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No SOPs found</p>
          <p className="text-sm text-slate-400">Try adjusting your search or category filter</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sops.map((sop, idx) => {
            const IconComponent = iconMap[sop.icon] || FileText;
            return (
              <motion.div
                key={sop.sop_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                whileHover={{ scale: 1.02, y: -4 }}
                onClick={() => { setSelectedSop(sop); setShowModal(true); }}
                className="bg-white rounded-2xl border border-slate-200 p-5 cursor-pointer hover:shadow-lg transition-all"
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${colorClasses[sop.color] || colorClasses.blue}`}>
                  <IconComponent className="w-6 h-6" />
                </div>
                <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
                  {sop.category}
                </span>
                <h3 className="font-semibold text-slate-800 mt-3 mb-2">{sop.title}</h3>
                <p className="text-sm text-slate-500 line-clamp-2">{sop.description}</p>
                <div className="flex items-center gap-2 mt-4 text-xs text-slate-400">
                  <Clock className="w-3.5 h-3.5" />
                  <span>Updated {formatDate(sop.updated_at)}</span>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Quick Tip */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-5 flex items-start gap-4"
      >
        <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
          <Lightbulb className="w-5 h-5 text-amber-600" />
        </div>
        <div>
          <h4 className="font-semibold text-amber-800">Pro Tip</h4>
          <p className="text-sm text-amber-700">
            Use the search bar to quickly find SOPs. Can&apos;t find what you need? Contact admin to request a new guide.
          </p>
        </div>
      </motion.div>

      {/* SOP Detail Modal - Enhanced Design */}
      <AnimatePresence>
        {showModal && selectedSop && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
            >
              {/* Header with gradient accent */}
              <div className="relative">
                <div className={`absolute inset-0 h-2 ${
                  selectedSop.color === 'blue' ? 'bg-gradient-to-r from-blue-500 to-blue-600' :
                  selectedSop.color === 'green' ? 'bg-gradient-to-r from-green-500 to-emerald-600' :
                  selectedSop.color === 'purple' ? 'bg-gradient-to-r from-purple-500 to-violet-600' :
                  selectedSop.color === 'amber' ? 'bg-gradient-to-r from-amber-500 to-orange-600' :
                  selectedSop.color === 'pink' ? 'bg-gradient-to-r from-pink-500 to-rose-600' :
                  selectedSop.color === 'indigo' ? 'bg-gradient-to-r from-indigo-500 to-blue-600' :
                  'bg-gradient-to-r from-blue-500 to-blue-600'
                }`} />
                <div className="pt-6 px-8 pb-5 flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 ${colorClasses[selectedSop.color] || colorClasses.blue}`}>
                      {(() => {
                        const IconComp = iconMap[selectedSop.icon] || FileText;
                        return <IconComp className="w-7 h-7" />;
                      })()}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                          selectedSop.color === 'blue' ? 'bg-blue-100 text-blue-700' :
                          selectedSop.color === 'green' ? 'bg-green-100 text-green-700' :
                          selectedSop.color === 'purple' ? 'bg-purple-100 text-purple-700' :
                          selectedSop.color === 'amber' ? 'bg-amber-100 text-amber-700' :
                          selectedSop.color === 'pink' ? 'bg-pink-100 text-pink-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {selectedSop.category}
                        </span>
                      </div>
                      <h3 className="text-2xl font-bold text-slate-800">{selectedSop.title}</h3>
                      <p className="text-slate-500 text-sm mt-1">{selectedSop.description}</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setShowModal(false)}
                    className="p-2.5 hover:bg-slate-100 rounded-xl text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Content area with better markdown styling */}
              <div className="flex-1 overflow-y-auto px-8 py-6 bg-gradient-to-b from-slate-50/50 to-white">
                <div className="max-w-none sop-content">
                  {(() => {
                    const lines = selectedSop.content?.split('\n') || [];
                    const elements = [];
                    let inList = false;
                    let listItems = [];
                    let inNumberedList = false;
                    let numberedItems = [];

                    const flushList = () => {
                      if (listItems.length > 0) {
                        elements.push(
                          <ul key={`list-${elements.length}`} className="space-y-2 my-4 ml-1">
                            {listItems.map((item, idx) => (
                              <li key={idx} className="flex items-start gap-3 text-slate-600">
                                <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2.5 flex-shrink-0" />
                                <span className="leading-relaxed">{item}</span>
                              </li>
                            ))}
                          </ul>
                        );
                        listItems = [];
                      }
                      if (numberedItems.length > 0) {
                        elements.push(
                          <ol key={`olist-${elements.length}`} className="space-y-3 my-4 ml-1">
                            {numberedItems.map((item, idx) => (
                              <li key={idx} className="flex items-start gap-3 text-slate-600">
                                <span className="w-6 h-6 rounded-lg bg-blue-100 text-blue-600 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                                  {idx + 1}
                                </span>
                                <span className="leading-relaxed">{item}</span>
                              </li>
                            ))}
                          </ol>
                        );
                        numberedItems = [];
                      }
                      inList = false;
                      inNumberedList = false;
                    };

                    lines.forEach((line, i) => {
                      // Main title (# )
                      if (line.startsWith('# ')) {
                        flushList();
                        elements.push(
                          <h1 key={i} className="text-2xl font-bold text-slate-800 mt-8 mb-4 pb-3 border-b-2 border-slate-200 first:mt-0">
                            {line.slice(2)}
                          </h1>
                        );
                      }
                      // Section headers (## )
                      else if (line.startsWith('## ')) {
                        flushList();
                        elements.push(
                          <h2 key={i} className="text-lg font-bold text-slate-800 mt-8 mb-3 flex items-center gap-2">
                            <span className="w-1 h-6 bg-blue-500 rounded-full" />
                            {line.slice(3)}
                          </h2>
                        );
                      }
                      // Subsection headers (### )
                      else if (line.startsWith('### ')) {
                        flushList();
                        elements.push(
                          <h3 key={i} className="text-base font-semibold text-slate-700 mt-6 mb-2">
                            {line.slice(4)}
                          </h3>
                        );
                      }
                      // Subsubsection headers (#### )
                      else if (line.startsWith('#### ')) {
                        flushList();
                        elements.push(
                          <h4 key={i} className="text-sm font-semibold text-slate-600 mt-4 mb-2 uppercase tracking-wide">
                            {line.slice(5)}
                          </h4>
                        );
                      }
                      // Horizontal rules
                      else if (line.trim() === '---' || line.trim() === '***') {
                        flushList();
                        elements.push(<hr key={i} className="my-6 border-slate-200" />);
                      }
                      // Bullet lists
                      else if (line.startsWith('* ') || line.startsWith('- ')) {
                        if (!inList) {
                          flushList();
                          inList = true;
                        }
                        listItems.push(line.slice(2));
                      }
                      // Numbered lists
                      else if (/^\d+\.\s/.test(line)) {
                        if (!inNumberedList) {
                          flushList();
                          inNumberedList = true;
                        }
                        numberedItems.push(line.replace(/^\d+\.\s/, ''));
                      }
                      // Bold text blocks (for emphasis like **text**)
                      else if (line.trim() === '') {
                        flushList();
                        // Don't add too many breaks
                      }
                      // Regular paragraphs
                      else if (line.trim()) {
                        flushList();
                        // Handle inline bold with **text**
                        const parts = line.split(/(\*\*[^*]+\*\*)/g);
                        const formattedLine = parts.map((part, pidx) => {
                          if (part.startsWith('**') && part.endsWith('**')) {
                            return <strong key={pidx} className="font-semibold text-slate-800">{part.slice(2, -2)}</strong>;
                          }
                          return part;
                        });
                        elements.push(
                          <p key={i} className="text-slate-600 leading-relaxed my-3">
                            {formattedLine}
                          </p>
                        );
                      }
                    });

                    flushList();
                    return elements;
                  })()}
                </div>
              </div>

              {/* Footer with actions */}
              <div className="px-8 py-4 border-t border-slate-200 bg-white flex items-center justify-between">
                <div className="flex items-center gap-4 text-sm text-slate-500">
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-4 h-4" />
                    <span>Updated {formatDate(selectedSop.updated_at)}</span>
                  </div>
                  {selectedSop.created_by && (
                    <div className="flex items-center gap-1.5">
                      <User className="w-4 h-4" />
                      <span>By {selectedSop.created_by}</span>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="px-5 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-sm font-medium transition-colors"
                >
                  Done
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ============ AGENCY HELP VIEW ============
function AgencyHelpView() {
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [message, setMessage] = useState('');
  const [subject, setSubject] = useState('');
  const [priority, setPriority] = useState('medium');
  const [submitting, setSubmitting] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);

  const commonIssues = [
    { id: 'google_ads', title: 'Google Ads access issue', icon: Target },
    { id: 'facebook_ads', title: 'Facebook Ads not showing', icon: Facebook },
    { id: 'dashboard', title: 'Client dashboard problem', icon: LayoutDashboard },
    { id: 'landing_page', title: 'Landing page not loading', icon: Globe },
    { id: 'reporting', title: 'Reporting data incorrect', icon: BarChart3 },
    { id: 'other', title: 'Other issue', icon: HelpCircle },
  ];

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/staff/support-tickets`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setTickets(data.tickets || []);
      }
    } catch (err) {
      console.error('Error fetching tickets:', err);
    } finally {
      setLoading(false);
    }
  };

  const submitTicket = async () => {
    if (!selectedIssue || !message.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/staff/support-tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          issue_type: selectedIssue.id,
          subject: subject || selectedIssue.title,
          description: message,
          priority: priority
        })
      });
      if (res.ok) {
        setShowSuccess(true);
        setMessage('');
        setSubject('');
        setSelectedIssue(null);
        setPriority('medium');
        fetchTickets();
        setTimeout(() => setShowSuccess(false), 3000);
      }
    } catch (err) {
      console.error('Error submitting ticket:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  const statusColors = {
    open: 'bg-blue-100 text-blue-700',
    in_progress: 'bg-amber-100 text-amber-700',
    resolved: 'bg-green-100 text-green-700',
    closed: 'bg-slate-100 text-slate-700'
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
          <LifeBuoy className="w-6 h-6 text-blue-500" />
          Get Help
        </h2>
        <p className="text-slate-500 text-sm">Need assistance? Create a support ticket or find answers</p>
      </div>

      {/* Success Message */}
      <AnimatePresence>
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3"
          >
            <Check className="w-5 h-5 text-green-500" />
            <p className="text-green-700 font-medium">Support ticket submitted! Admin will respond within 1-2 hours.</p>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Help Cards */}
        <div className="lg:col-span-2 space-y-4">
          <h3 className="font-semibold text-slate-800">What do you need help with?</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {commonIssues.map((issue) => {
              const Icon = issue.icon;
              return (
                <motion.button
                  key={issue.id}
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSelectedIssue(issue)}
                  className={`p-4 rounded-xl text-left transition-all border-2 ${
                    selectedIssue?.id === issue.id
                      ? 'bg-blue-50 border-blue-500'
                      : 'bg-white border-slate-200 hover:border-blue-300'
                  }`}
                >
                  <Icon className={`w-6 h-6 mb-2 ${
                    selectedIssue?.id === issue.id ? 'text-blue-500' : 'text-slate-400'
                  }`} />
                  <p className="text-sm font-medium text-slate-800">{issue.title}</p>
                </motion.button>
              );
            })}
          </div>

          {/* Message Form */}
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="bg-white border border-slate-200 rounded-2xl p-5 mt-4"
          >
            <h3 className="font-semibold text-slate-800 mb-3">
              {selectedIssue ? `Describe your ${selectedIssue.title.toLowerCase()}` : 'Describe your issue'}
            </h3>
            
            <div className="space-y-3">
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Subject (optional)"
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
              
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Please provide details about your issue..."
                rows={4}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
              
              <div className="flex items-center gap-2">
                <label className="text-sm text-slate-600">Priority:</label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>
            
            <div className="flex items-center justify-between mt-4">
              <p className="text-xs text-slate-500">Admin will respond within 1-2 hours</p>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={submitTicket}
                disabled={!selectedIssue || !message.trim() || submitting}
                className="px-6 py-2.5 bg-blue-500 hover:bg-blue-600 disabled:bg-slate-300 text-white text-sm font-semibold rounded-xl transition-colors flex items-center gap-2"
              >
                {submitting ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                {submitting ? 'Sending...' : 'Submit Ticket'}
              </motion.button>
            </div>
          </motion.div>

          {/* My Tickets */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <h3 className="font-semibold text-slate-800 mb-4">My Support Tickets</h3>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-5 h-5 animate-spin text-blue-500" />
              </div>
            ) : tickets.length === 0 ? (
              <div className="text-center py-8">
                <Inbox className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No support tickets yet</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {tickets.slice(0, 5).map((ticket) => (
                  <div key={ticket.ticket_id} className="p-3 bg-slate-50 rounded-xl">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-slate-800">{ticket.subject}</p>
                        <p className="text-xs text-slate-500 mt-1">{ticket.ticket_id} • {formatDate(ticket.created_at)}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${statusColors[ticket.status]}`}>
                        {ticket.status.replace('_', ' ')}
                      </span>
                    </div>
                    {ticket.responses?.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-slate-200">
                        <p className="text-xs text-green-600 flex items-center gap-1">
                          <Check className="w-3 h-3" />
                          Admin responded
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Contact Admin Card */}
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl p-5 text-white"
          >
            <Headphones className="w-10 h-10 mb-3 opacity-80" />
            <h3 className="font-semibold text-lg mb-1">Need urgent help?</h3>
            <p className="text-blue-100 text-sm mb-4">Set priority to &quot;Urgent&quot; for immediate attention</p>
            <button 
              onClick={() => { setSelectedIssue(commonIssues[5]); setPriority('urgent'); }}
              className="w-full py-2.5 bg-white text-blue-600 font-semibold rounded-xl hover:bg-blue-50 transition-colors"
            >
              Create Urgent Ticket
            </button>
          </motion.div>

          {/* Quick Links */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <h3 className="font-semibold text-slate-800 mb-3">Quick Resources</h3>
            <div className="space-y-2">
              {[
                { label: 'View SOPs', icon: BookOpen, section: 'agency-sop' },
                { label: 'Training Videos', icon: Video, section: null },
                { label: 'FAQ', icon: FileQuestion, section: null },
                { label: 'Team Chat', icon: MessageCircle, section: 'messages' },
              ].map((item) => (
                <button
                  key={item.label}
                  className="w-full flex items-center gap-3 p-3 text-left hover:bg-slate-50 rounded-xl transition-colors"
                >
                  <item.icon className="w-5 h-5 text-slate-400" />
                  <span className="text-sm font-medium text-slate-700">{item.label}</span>
                  <ChevronRight className="w-4 h-4 text-slate-400 ml-auto" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ============ AGENCY TIME TRACKING VIEW ============
function AgencyTimeTrackingView({ clients = [] }) {
  const [isTracking, setIsTracking] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedClientName, setSelectedClientName] = useState('');
  const [selectedTask, setSelectedTask] = useState('');
  const [taskDescription, setTaskDescription] = useState('');
  const [isBillable, setIsBillable] = useState(true);
  const [trackingStartTime, setTrackingStartTime] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ total_hours: 0, billable_hours: 0, total_entries: 0 });
  const timerRef = useRef(null);

  const taskTypes = [
    { id: 'ad_optimization', label: 'Ad Optimization' },
    { id: 'landing_page', label: 'Landing Page Work' },
    { id: 'reporting', label: 'Reporting' },
    { id: 'client_meeting', label: 'Client Meeting' },
    { id: 'strategy', label: 'Strategy & Planning' },
    { id: 'creative', label: 'Creative Development' },
    { id: 'other', label: 'Other' },
  ];

  useEffect(() => {
    fetchEntries();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  useEffect(() => {
    if (isTracking) {
      timerRef.current = setInterval(() => {
        setElapsedSeconds(prev => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isTracking]);

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      const res = await fetch(`${API}/api/staff/time-entries?start_date=${weekAgo}&end_date=${today}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setEntries(data.entries || []);
        setSummary(data.summary || {});
      }
    } catch (err) {
      console.error('Error fetching time entries:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const startTracking = () => {
    if (!selectedClient || !selectedTask) return;
    setTrackingStartTime(new Date());
    setElapsedSeconds(0);
    setIsTracking(true);
  };

  const stopTracking = async () => {
    setIsTracking(false);
    const endTime = new Date();
    
    try {
      const res = await fetch(`${API}/api/staff/time-entries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          client_id: selectedClient,
          client_name: selectedClientName,
          task_type: selectedTask,
          description: taskDescription,
          start_time: trackingStartTime.toISOString(),
          end_time: endTime.toISOString(),
          duration_minutes: Math.round(elapsedSeconds / 60),
          is_billable: isBillable
        })
      });
      if (res.ok) {
        fetchEntries();
        setSelectedClient('');
        setSelectedClientName('');
        setSelectedTask('');
        setTaskDescription('');
        setElapsedSeconds(0);
      }
    } catch (err) {
      console.error('Error saving time entry:', err);
    }
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '0m';
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hrs === 0) return `${mins}m`;
    return `${hrs}h ${mins}m`;
  };

  const formatEntryTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const formatEntryDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    if (date.toDateString() === today.toDateString()) return 'Today';
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const statusColors = {
    pending: 'bg-amber-100 text-amber-700',
    approved: 'bg-green-100 text-green-700',
    denied: 'bg-red-100 text-red-700'
  };

  const todayEntries = entries.filter(e => {
    const entryDate = new Date(e.start_time).toDateString();
    return entryDate === new Date().toDateString();
  });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
          <Timer className="w-6 h-6 text-blue-500" />
          Time Tracking
        </h2>
        <p className="text-slate-500 text-sm">Track time spent on client work</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Timer Section */}
        <div className="lg:col-span-2 space-y-4">
          {/* Active Timer */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`rounded-2xl p-6 ${
              isTracking 
                ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white' 
                : 'bg-white border border-slate-200'
            }`}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className={`text-sm ${isTracking ? 'text-green-100' : 'text-slate-500'}`}>
                  {isTracking ? 'Currently tracking time for' : 'Start a new time entry'}
                </p>
                {isTracking && (
                  <p className="font-semibold mt-1">{selectedClientName} - {taskTypes.find(t => t.id === selectedTask)?.label}</p>
                )}
              </div>
              <motion.div
                animate={isTracking ? { scale: [1, 1.1, 1] } : {}}
                transition={{ repeat: Infinity, duration: 2 }}
                className={`w-3 h-3 rounded-full ${isTracking ? 'bg-white' : 'bg-slate-300'}`}
              />
            </div>
            
            <div className="text-center py-6">
              <motion.p 
                className={`text-5xl font-bold font-mono ${isTracking ? 'text-white' : 'text-slate-800'}`}
                animate={isTracking ? { opacity: [1, 0.7, 1] } : {}}
                transition={{ repeat: Infinity, duration: 1.5 }}
              >
                {formatTime(elapsedSeconds)}
              </motion.p>
            </div>

            {!isTracking && (
              <div className="space-y-3 mb-4">
                <div className="grid grid-cols-2 gap-4">
                  <select
                    value={selectedClient}
                    onChange={(e) => {
                      setSelectedClient(e.target.value);
                      const client = clients.find(c => c.client_id === e.target.value);
                      setSelectedClientName(client?.business_name || '');
                    }}
                    className="px-4 py-2.5 border border-slate-200 rounded-xl text-sm"
                  >
                    <option value="">Select Client</option>
                    {clients.map(c => (
                      <option key={c.client_id} value={c.client_id}>{c.business_name}</option>
                    ))}
                  </select>
                  <select
                    value={selectedTask}
                    onChange={(e) => setSelectedTask(e.target.value)}
                    className="px-4 py-2.5 border border-slate-200 rounded-xl text-sm"
                  >
                    <option value="">Select Task</option>
                    {taskTypes.map(t => (
                      <option key={t.id} value={t.id}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <input
                  type="text"
                  value={taskDescription}
                  onChange={(e) => setTaskDescription(e.target.value)}
                  placeholder="Description (optional)"
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm"
                />
                <label className="flex items-center gap-2 text-sm text-slate-600">
                  <input
                    type="checkbox"
                    checked={isBillable}
                    onChange={(e) => setIsBillable(e.target.checked)}
                    className="rounded border-slate-300"
                  />
                  Billable time
                </label>
              </div>
            )}

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={isTracking ? stopTracking : startTracking}
              disabled={!isTracking && (!selectedClient || !selectedTask)}
              className={`w-full py-3 font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 ${
                isTracking 
                  ? 'bg-white text-green-600 hover:bg-green-50' 
                  : 'bg-blue-500 text-white hover:bg-blue-600 disabled:bg-slate-300 disabled:cursor-not-allowed'
              }`}
            >
              {isTracking ? (
                <>
                  <Pause className="w-5 h-5" />
                  Stop & Save
                </>
              ) : (
                <>
                  <Play className="w-5 h-5" />
                  Start Timer
                </>
              )}
            </motion.button>
          </motion.div>

          {/* Today's Entries */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-800">Today&apos;s Entries</h3>
              <button onClick={fetchEntries} className="p-2 hover:bg-slate-100 rounded-lg">
                <RefreshCw className={`w-4 h-4 text-slate-400 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-5 h-5 animate-spin text-blue-500" />
              </div>
            ) : todayEntries.length === 0 ? (
              <div className="text-center py-8">
                <Clock className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No time entries today</p>
                <p className="text-sm text-slate-400">Start tracking to log your work</p>
              </div>
            ) : (
              <div className="space-y-3">
                {todayEntries.map((entry) => (
                  <div key={entry.entry_id} className="flex items-center gap-4 p-3 bg-slate-50 rounded-xl">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-800 truncate">{entry.client_name}</p>
                      <p className="text-sm text-slate-500">{taskTypes.find(t => t.id === entry.task_type)?.label || entry.task_type} • Started {formatEntryTime(entry.start_time)}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-slate-800">{formatDuration(entry.duration_minutes)}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${statusColors[entry.status]}`}>
                        {entry.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar Stats */}
        <div className="space-y-4">
          {/* Weekly Summary */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-slate-200 rounded-2xl p-5"
          >
            <h3 className="font-semibold text-slate-800 mb-4">This Week</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-slate-500">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm">Total Hours</span>
                </div>
                <span className="font-bold text-slate-800">{summary.total_hours || 0}h</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-slate-500">
                  <DollarSign className="w-4 h-4" />
                  <span className="text-sm">Billable</span>
                </div>
                <span className="font-bold text-green-600">{summary.billable_hours || 0}h</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-slate-500">
                  <FileText className="w-4 h-4" />
                  <span className="text-sm">Entries</span>
                </div>
                <span className="font-bold text-slate-800">{summary.total_entries || 0}</span>
              </div>
            </div>
          </motion.div>

          {/* Recent Entries */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <h3 className="font-semibold text-slate-800 mb-4">Recent Entries</h3>
            <div className="space-y-3 max-h-48 overflow-y-auto">
              {entries.slice(0, 5).map((entry) => (
                <div key={entry.entry_id} className="flex items-center gap-3 text-sm">
                  <div className={`w-2 h-2 rounded-full ${entry.status === 'approved' ? 'bg-green-500' : entry.status === 'denied' ? 'bg-red-500' : 'bg-amber-500'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-700 truncate">{entry.client_name}</p>
                    <p className="text-xs text-slate-400">{formatEntryDate(entry.start_time)}</p>
                  </div>
                  <span className="font-medium text-slate-600">{formatDuration(entry.duration_minutes)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Export Button */}
          <button className="w-full py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-xl transition-colors flex items-center justify-center gap-2">
            <Download className="w-4 h-4" />
            Export Timesheet
          </button>
        </div>
      </div>
    </motion.div>
  );
}

// ============ CLIENT LANDING PAGE TAB ============
function ClientLandingPageTab({ client }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-800">Landing Page</h3>
        <div className="flex gap-2">
          <motion.a
            href={client.landing_page_url || '#'}
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium rounded-xl flex items-center gap-2"
          >
            <ExternalLink className="w-4 h-4" />
            View Live Page
          </motion.a>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-xl flex items-center gap-2"
          >
            <Edit3 className="w-4 h-4" />
            Edit Page
          </motion.button>
        </div>
      </div>

      {/* Landing Page Preview */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white border border-slate-200 rounded-2xl overflow-hidden"
      >
        <div className="bg-slate-100 p-3 flex items-center gap-2 border-b border-slate-200">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-red-400" />
            <div className="w-3 h-3 rounded-full bg-amber-400" />
            <div className="w-3 h-3 rounded-full bg-green-400" />
          </div>
          <div className="flex-1 bg-white rounded-lg px-3 py-1 text-xs text-slate-500 font-mono">
            {client.landing_page_url || 'https://example.com/landing-page'}
          </div>
        </div>
        <div className="aspect-video bg-slate-50 flex items-center justify-center">
          <div className="text-center p-8">
            <Globe className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Landing page preview</p>
            <p className="text-xs text-slate-400 mt-1">Click "View Live Page" to see the actual page</p>
          </div>
        </div>
      </motion.div>

      {/* Page Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Page Views', value: '2,847', change: '+12%' },
          { label: 'Conversions', value: '142', change: '+8%' },
          { label: 'Conv. Rate', value: '4.98%', change: '+0.5%' },
          { label: 'Avg. Time', value: '2:34', change: '-5s' },
        ].map((stat) => (
          <motion.div
            key={stat.label}
            whileHover={{ y: -2 }}
            className="bg-white border border-slate-200 rounded-xl p-4"
          >
            <p className="text-xs text-slate-500">{stat.label}</p>
            <p className="text-2xl font-bold text-slate-800">{stat.value}</p>
            <p className="text-xs text-green-500">{stat.change} vs last month</p>
          </motion.div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5">
        <h4 className="font-semibold text-slate-800 mb-4">Landing Page Actions</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { icon: Edit3, label: 'Edit Content', color: 'blue' },
            { icon: Image, label: 'Update Images', color: 'purple' },
            { icon: Settings, label: 'Page Settings', color: 'slate' },
            { icon: BarChart3, label: 'View Analytics', color: 'green' },
          ].map((action) => (
            <motion.button
              key={action.label}
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
              className={`p-4 bg-white border border-slate-200 rounded-xl text-center hover:border-${action.color}-300 transition-all`}
            >
              <action.icon className={`w-6 h-6 text-${action.color}-500 mx-auto mb-2`} />
              <p className="text-sm font-medium text-slate-700">{action.label}</p>
            </motion.button>
          ))}
        </div>
      </div>
    </div>
  );
}


// ============ BOOKING HUB SECTION ============
function BookingHubSection({ staffUser }) {
  const [activeTab, setActiveTab] = useState('my-calendar');
  const [appointments, setAppointments] = useState([]);
  const [allAppointments, setAllAppointments] = useState([]);
  const [invites, setInvites] = useState([]);
  const [meetingTypes, setMeetingTypes] = useState([]);
  const [staffAvailability, setStaffAvailability] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [inviteForm, setInviteForm] = useState({ lead_name: '', lead_email: '' });
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [copiedLink, setCopiedLink] = useState(null);
  const [calendarView, setCalendarView] = useState('week');
  const [calendarDate, setCalendarDate] = useState(new Date());
  
  const isAdmin = staffUser?.role === 'agency_director' || staffUser?.role === 'admin';

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'my-calendar') {
        const res = await fetch(`${API}/api/staff/booking/appointments`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setAppointments(data.appointments || []);
        }
      } else if (activeTab === 'global-calendar' && isAdmin) {
        const res = await fetch(`${API}/api/staff/admin/booking/appointments`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setAllAppointments(data.appointments || []);
        }
      } else if (activeTab === 'booking-links') {
        const res = await fetch(`${API}/api/staff/booking/invites`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setInvites(data.invites || []);
        }
      } else if (activeTab === 'settings' && isAdmin) {
        const [typesRes, availRes] = await Promise.all([
          fetch(`${API}/api/staff/admin/booking/meeting-types`, { credentials: 'include' }),
          fetch(`${API}/api/staff/admin/booking/staff-availability`, { credentials: 'include' })
        ]);
        if (typesRes.ok) {
          const data = await typesRes.json();
          setMeetingTypes(data.meeting_types || []);
        }
        if (availRes.ok) {
          const data = await availRes.json();
          setStaffAvailability(data.availability || []);
        }
      }
    } catch (err) {
      console.error('Error fetching booking data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Convert appointments to calendar events
  const calendarEvents = useMemo(() => {
    const sourceAppointments = activeTab === 'global-calendar' ? allAppointments : appointments;
    return sourceAppointments.map(apt => ({
      id: apt.appointment_id,
      title: `${apt.title} - ${apt.client_name}`,
      start: new Date(apt.start_time),
      end: new Date(apt.end_time),
      resource: apt,
      color: apt.meeting_type === 'strategy' ? '#3b82f6' :
             apt.meeting_type === 'onboarding' ? '#10b981' :
             apt.meeting_type === 'support' ? '#f59e0b' : '#8b5cf6'
    }));
  }, [appointments, allAppointments, activeTab]);

  const eventStyleGetter = (event) => ({
    style: {
      backgroundColor: event.color,
      borderRadius: '6px',
      opacity: 0.9,
      color: 'white',
      border: 'none',
      fontSize: '12px',
      padding: '2px 6px'
    }
  });

  const handleSelectEvent = (event) => {
    setSelectedEvent(event.resource);
    setShowEventModal(true);
  };

  const createInvite = async () => {
    if (!inviteForm.lead_name || !inviteForm.lead_email) return;
    setCreatingInvite(true);
    try {
      const res = await fetch(
        `${API}/api/staff/booking/invites?lead_name=${encodeURIComponent(inviteForm.lead_name)}&lead_email=${encodeURIComponent(inviteForm.lead_email)}&expires_days=7`,
        { method: 'POST', credentials: 'include' }
      );
      if (res.ok) {
        setShowInviteModal(false);
        setInviteForm({ lead_name: '', lead_email: '' });
        fetchData();
      }
    } catch (err) {
      console.error('Error creating invite:', err);
    } finally {
      setCreatingInvite(false);
    }
  };

  const copyLink = (link, id) => {
    navigator.clipboard.writeText(`${window.location.origin}${link}`);
    setCopiedLink(id);
    setTimeout(() => setCopiedLink(null), 2000);
  };

  const formatDateTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const tabs = [
    { id: 'my-calendar', label: 'My Calendar', icon: Calendar },
    ...(isAdmin ? [{ id: 'global-calendar', label: 'Global Calendar', icon: Users }] : []),
    { id: 'booking-links', label: 'Booking Links', icon: Link },
    ...(isAdmin ? [{ id: 'settings', label: 'Settings', icon: Settings }] : []),
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Tabs */}
      <div className="bg-white rounded-2xl border border-slate-200 p-1 inline-flex gap-1">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all flex items-center gap-2 ${
              activeTab === tab.id
                ? 'bg-blue-600 text-white'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {/* My Calendar */}
        {activeTab === 'my-calendar' && (
          <motion.div
            key="my-calendar"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-4"
          >
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-800">My Calendar</h3>
                <div className="flex items-center gap-2">
                  <div className="flex bg-slate-100 rounded-lg p-1">
                    {['day', 'week', 'month'].map((view) => (
                      <button
                        key={view}
                        onClick={() => setCalendarView(view)}
                        className={`px-3 py-1 rounded-md text-xs font-medium capitalize transition-all ${
                          calendarView === view ? 'bg-white shadow text-slate-800' : 'text-slate-500'
                        }`}
                      >
                        {view}
                      </button>
                    ))}
                  </div>
                  <button 
                    onClick={fetchData}
                    className="p-2 hover:bg-slate-100 rounded-lg text-slate-500"
                  >
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  </button>
                </div>
              </div>
              
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
                </div>
              ) : (
                <div className="h-[500px]">
                  <BigCalendar
                    localizer={localizer}
                    events={calendarEvents}
                    startAccessor="start"
                    endAccessor="end"
                    view={calendarView}
                    onView={setCalendarView}
                    date={calendarDate}
                    onNavigate={setCalendarDate}
                    eventPropGetter={eventStyleGetter}
                    onSelectEvent={handleSelectEvent}
                    views={['day', 'week', 'month']}
                    style={{ height: '100%' }}
                    popup
                    toolbar={true}
                  />
                </div>
              )}
            </div>

            {/* Upcoming Appointments List */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Upcoming Appointments</h3>
              {appointments.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No upcoming appointments</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {appointments.slice(0, 5).map((apt) => (
                    <motion.div
                      key={apt.appointment_id}
                      whileHover={{ scale: 1.01 }}
                      onClick={() => { setSelectedEvent(apt); setShowEventModal(true); }}
                      className="p-4 bg-slate-50 rounded-xl border border-slate-200 flex items-center gap-4 cursor-pointer hover:border-blue-300 transition-all"
                    >
                      <div 
                        className="w-1 h-14 rounded-full"
                        style={{ backgroundColor: apt.meeting_type === 'strategy' ? '#3b82f6' :
                          apt.meeting_type === 'onboarding' ? '#10b981' :
                          apt.meeting_type === 'support' ? '#f59e0b' : '#8b5cf6' }}
                      />
                      <div className="flex-1">
                        <h4 className="font-medium text-slate-800">{apt.title}</h4>
                        <p className="text-sm text-slate-500">{apt.client_name} • {apt.client_email}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDateTime(apt.start_time)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Video className="w-3 h-3" />
                            {apt.location_type?.replace('_', ' ')}
                          </span>
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        apt.status === 'scheduled' ? 'bg-green-100 text-green-700' :
                        apt.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                        'bg-slate-100 text-slate-700'
                      }`}>
                        {apt.status}
                      </span>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Global Calendar (Admin only) */}
        {activeTab === 'global-calendar' && isAdmin && (
          <motion.div
            key="global-calendar"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-4"
          >
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-800">Team Calendar</h3>
                <div className="flex items-center gap-2">
                  <div className="flex bg-slate-100 rounded-lg p-1">
                    {['day', 'week', 'month'].map((view) => (
                      <button
                        key={view}
                        onClick={() => setCalendarView(view)}
                        className={`px-3 py-1 rounded-md text-xs font-medium capitalize transition-all ${
                          calendarView === view ? 'bg-white shadow text-slate-800' : 'text-slate-500'
                        }`}
                      >
                        {view}
                      </button>
                    ))}
                  </div>
                  <button 
                    onClick={fetchData}
                    className="p-2 hover:bg-slate-100 rounded-lg text-slate-500"
                  >
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  </button>
                </div>
              </div>
              
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
                </div>
              ) : (
                <div className="h-[500px]">
                  <BigCalendar
                    localizer={localizer}
                    events={calendarEvents}
                    startAccessor="start"
                    endAccessor="end"
                    view={calendarView}
                    onView={setCalendarView}
                    date={calendarDate}
                    onNavigate={setCalendarDate}
                    eventPropGetter={eventStyleGetter}
                    onSelectEvent={handleSelectEvent}
                    views={['day', 'week', 'month']}
                    style={{ height: '100%' }}
                    popup
                    toolbar={true}
                  />
                </div>
              )}
            </div>

            {/* All Team Appointments */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">All Team Appointments</h3>
              {allAppointments.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No team appointments found</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {allAppointments.slice(0, 10).map((apt) => (
                    <motion.div
                      key={apt.appointment_id}
                      whileHover={{ scale: 1.01 }}
                      onClick={() => { setSelectedEvent(apt); setShowEventModal(true); }}
                      className="p-4 bg-slate-50 rounded-xl border border-slate-200 flex items-center gap-4 cursor-pointer hover:border-blue-300 transition-all"
                    >
                      <div 
                        className="w-1 h-14 rounded-full"
                        style={{ backgroundColor: apt.meeting_type === 'strategy' ? '#3b82f6' :
                          apt.meeting_type === 'onboarding' ? '#10b981' :
                          apt.meeting_type === 'support' ? '#f59e0b' : '#8b5cf6' }}
                      />
                      <div className="flex-1">
                        <h4 className="font-medium text-slate-800">{apt.title}</h4>
                        <p className="text-sm text-slate-500">
                          {apt.client_name} with <span className="font-medium">{apt.staff_name || apt.staff_username}</span>
                        </p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDateTime(apt.start_time)}
                          </span>
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        apt.status === 'scheduled' ? 'bg-green-100 text-green-700' :
                        apt.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                        'bg-slate-100 text-slate-700'
                      }`}>
                        {apt.status}
                      </span>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Booking Links */}
        {activeTab === 'booking-links' && (
          <motion.div
            key="booking-links"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-4"
          >
            {/* Quick Links */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Your Booking Links</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Link className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-800">Your Personal Link</h4>
                      <p className="text-xs text-slate-500">Clients book directly with you</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <code className="flex-1 px-3 py-2 bg-white rounded-lg text-sm text-slate-600 border border-blue-200 truncate">
                      {window.location.origin}/book?staff={staffUser?.username}
                    </code>
                    <button
                      onClick={() => copyLink(`/book?staff=${staffUser?.username}`, 'personal')}
                      className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium flex items-center gap-1"
                    >
                      {copiedLink === 'personal' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      {copiedLink === 'personal' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>

                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                      <Globe className="w-5 h-5 text-slate-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-800">Global Booking Link</h4>
                      <p className="text-xs text-slate-500">Clients choose any available staff</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <code className="flex-1 px-3 py-2 bg-white rounded-lg text-sm text-slate-600 border border-slate-200 truncate">
                      {window.location.origin}/book
                    </code>
                    <button
                      onClick={() => copyLink('/book', 'global')}
                      className="px-3 py-2 bg-slate-600 hover:bg-slate-700 text-white rounded-lg text-sm font-medium flex items-center gap-1"
                    >
                      {copiedLink === 'global' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      {copiedLink === 'global' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Strategy Call Invites */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-800">Strategy Call Invites</h3>
                  <p className="text-sm text-slate-500">Create private booking links for leads</p>
                </div>
                <button
                  onClick={() => setShowInviteModal(true)}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Create Invite
                </button>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
                </div>
              ) : invites.length === 0 ? (
                <div className="text-center py-8">
                  <Mail className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No invites created yet</p>
                  <p className="text-sm text-slate-400">Create an invite to share a private booking link</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {invites.map((invite) => (
                    <div 
                      key={invite.invite_id}
                      className={`p-4 rounded-xl border ${invite.used ? 'bg-slate-50 border-slate-200' : 'bg-green-50 border-green-200'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-slate-800">{invite.lead_name}</h4>
                          <p className="text-sm text-slate-500">{invite.lead_email}</p>
                          <p className="text-xs text-slate-400 mt-1">
                            Created {new Date(invite.created_at).toLocaleDateString()} • 
                            Expires {new Date(invite.expires_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {invite.used ? (
                            <span className="px-3 py-1 bg-slate-200 text-slate-600 rounded-full text-xs font-medium">
                              Used
                            </span>
                          ) : (
                            <>
                              <button
                                onClick={() => copyLink(invite.booking_url, invite.invite_id)}
                                className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-medium flex items-center gap-1"
                              >
                                {copiedLink === invite.invite_id ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                                {copiedLink === invite.invite_id ? 'Copied!' : 'Copy Link'}
                              </button>
                              <span className="px-3 py-1 bg-green-200 text-green-700 rounded-full text-xs font-medium">
                                Active
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Settings (Admin only) */}
        {activeTab === 'settings' && isAdmin && (
          <motion.div
            key="settings"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-6"
          >
            {/* Meeting Types */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-800">Meeting Types</h3>
                  <p className="text-sm text-slate-500">Configure available meeting types and durations</p>
                </div>
                <button 
                  onClick={fetchData}
                  className="p-2 hover:bg-slate-100 rounded-lg text-slate-500"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
              </div>
              
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
                </div>
              ) : (
                <div className="space-y-3">
                  {meetingTypes.map((type) => (
                    <div 
                      key={type.type_id}
                      className="p-4 bg-slate-50 rounded-xl border border-slate-200 flex items-center gap-4"
                    >
                      <div 
                        className="w-3 h-12 rounded-full"
                        style={{ backgroundColor: type.color }}
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-slate-800">{type.name}</h4>
                          {type.requires_invite && (
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                              Invite Only
                            </span>
                          )}
                          {type.requires_client_login && (
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                              Client Login
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-500">{type.description}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-slate-800">{type.duration_min} min</p>
                        <p className={`text-xs ${type.is_active ? 'text-green-600' : 'text-slate-400'}`}>
                          {type.is_active ? 'Active' : 'Inactive'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Staff Availability */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-800">Staff Availability</h3>
                  <p className="text-sm text-slate-500">View and manage team availability settings</p>
                </div>
              </div>
              
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
                </div>
              ) : staffAvailability.length === 0 ? (
                <div className="text-center py-8">
                  <Clock className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No availability configured</p>
                  <p className="text-sm text-slate-400">Staff members can set their availability in their settings</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {staffAvailability.map((avail) => (
                    <div 
                      key={avail.staff_username}
                      className="p-4 bg-slate-50 rounded-xl border border-slate-200"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold">
                            {avail.staff_name?.charAt(0) || avail.staff_username?.charAt(0)?.toUpperCase()}
                          </div>
                          <div>
                            <h4 className="font-medium text-slate-800">{avail.staff_name || avail.staff_username}</h4>
                            <p className="text-xs text-slate-500">{avail.timezone}</p>
                          </div>
                        </div>
                        <div className="text-right text-xs text-slate-400">
                          <p>Buffer: {avail.buffers?.before_min || 10}min before / {avail.buffers?.after_min || 10}min after</p>
                          <p>Book up to {avail.max_days_ahead || 30} days ahead</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-7 gap-1 text-center text-xs">
                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, idx) => {
                          const dayKey = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'][idx];
                          const hours = avail.weekly_hours?.[dayKey] || [];
                          const hasHours = hours.length > 0;
                          return (
                            <div 
                              key={day}
                              className={`py-2 rounded-lg ${hasHours ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-400'}`}
                            >
                              <p className="font-medium">{day}</p>
                              <p className="text-[10px]">{hasHours ? `${hours.length} slot${hours.length > 1 ? 's' : ''}` : 'Off'}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Info Note */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-800">Editing Coming Soon</p>
                  <p className="text-sm text-blue-700">
                    Full editing capabilities for meeting types and availability settings will be available in a future update.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create Invite Modal */}
      <AnimatePresence>
        {showInviteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowInviteModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl p-6 w-full max-w-md"
            >
              <h3 className="text-xl font-bold text-slate-800 mb-4">Create Strategy Call Invite</h3>
              <p className="text-sm text-slate-500 mb-6">
                Generate a unique booking link for a lead to schedule a strategy call with you.
              </p>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Lead Name</label>
                  <input
                    type="text"
                    value={inviteForm.lead_name}
                    onChange={(e) => setInviteForm({ ...inviteForm, lead_name: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Lead Email</label>
                  <input
                    type="email"
                    value={inviteForm.lead_email}
                    onChange={(e) => setInviteForm({ ...inviteForm, lead_email: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowInviteModal(false)}
                  className="flex-1 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={createInvite}
                  disabled={!inviteForm.lead_name || !inviteForm.lead_email || creatingInvite}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-lg font-medium flex items-center justify-center gap-2"
                >
                  {creatingInvite ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4" />
                  )}
                  Create Invite
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Event Detail Modal */}
      <AnimatePresence>
        {showEventModal && selectedEvent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowEventModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl p-6 w-full max-w-md"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-slate-800">{selectedEvent.title}</h3>
                <button 
                  onClick={() => setShowEventModal(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg text-slate-400"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center gap-3 text-slate-600">
                  <Calendar className="w-5 h-5 text-slate-400" />
                  <span>{formatDateTime(selectedEvent.start_time)}</span>
                </div>
                
                <div className="flex items-center gap-3 text-slate-600">
                  <Clock className="w-5 h-5 text-slate-400" />
                  <span>{selectedEvent.duration_min || 30} minutes</span>
                </div>
                
                <div className="flex items-center gap-3 text-slate-600">
                  <User className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="font-medium">{selectedEvent.client_name}</p>
                    <p className="text-sm text-slate-500">{selectedEvent.client_email}</p>
                  </div>
                </div>
                
                {selectedEvent.staff_name && (
                  <div className="flex items-center gap-3 text-slate-600">
                    <Users className="w-5 h-5 text-slate-400" />
                    <span>Assigned to: {selectedEvent.staff_name}</span>
                  </div>
                )}
                
                <div className="flex items-center gap-3 text-slate-600">
                  <Video className="w-5 h-5 text-slate-400" />
                  <span className="capitalize">{selectedEvent.location_type?.replace('_', ' ')}</span>
                </div>
                
                {selectedEvent.notes && (
                  <div className="pt-3 border-t border-slate-200">
                    <p className="text-sm font-medium text-slate-700 mb-1">Notes</p>
                    <p className="text-sm text-slate-600">{selectedEvent.notes}</p>
                  </div>
                )}
                
                <div className="pt-3 border-t border-slate-200">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    selectedEvent.status === 'scheduled' ? 'bg-green-100 text-green-700' :
                    selectedEvent.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                    selectedEvent.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                    'bg-slate-100 text-slate-700'
                  }`}>
                    {selectedEvent.status}
                  </span>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowEventModal(false)}
                  className="flex-1 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-medium"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
